import { useState, useRef, useEffect } from 'react';
import {
  BarChart, Bar, LineChart, Line, ScatterChart, Scatter,
  AreaChart, Area, PieChart, Pie, Cell, ComposedChart,
  XAxis, YAxis, ZAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';

// Placeholder — real headers fetched from /api/spreadsheet/data on mount.
// Kept as [] (not omitted) so the useState(INITIAL_HEADERS) signature stays
// the same; useEffect overwrites it with the API response.
const INITIAL_HEADERS = [];

// Placeholder — actual rows fetched from /api/spreadsheet/data on mount.
const INITIAL_DATA = [];

// Columns present in the data but hidden by default to keep the grid
// uncluttered — the user can reveal them via the Columns picker. The
// Etsy attribute columns live here (lots of detail, niche use).
// 'Listing Title' is the full Etsy title behind the short "Listing" name —
// there for text filters and the AI, too wide to show by default.
const DEFAULT_HIDDEN_COLS = ['Orientation', 'Primary Color', 'Secondary Color', 'Aspect Ratio',
  'Tags', 'Price', 'Home Style', 'Room', 'Subject', 'Who Made', 'Category', 'Shop Section',
  'Listing Title'];
// Saved settings carry the user's own hiddenCols, so a column added to
// DEFAULT_HIDDEN_COLS later would show for every returning user. Settings
// therefore record which defaults they already knew (knownDefaultHidden);
// on load, any default the saved copy never saw starts hidden. Settings
// saved before that field existed knew this list.
const LEGACY_KNOWN_DEFAULT_HIDDEN = ['Orientation', 'Primary Color', 'Secondary Color',
  'Aspect Ratio', 'Tags', 'Price', 'Home Style', 'Room', 'Subject', 'Who Made',
  'Category', 'Shop Section'];

// Column groups for the data-scope toggle (Both / Etsy / Pinterest).
// Etsy scope collapses to one row per listing and hides the per-pin
// (Pinterest) columns; Pinterest scope keeps per-pin rows and hides the
// listing-level Etsy columns (Listing title stays for context). Listing,
// Shop, and Listing Key are common identity and shown in every scope.
const PIN_COLS = ['Pin #', 'Type', 'Status', 'Board', 'Impressions', 'Saves',
  'Pin Clicks', 'Outbound', 'CTR %', 'Verdict', 'Posted At', 'Pin Title'];
const LISTING_ONLY_COLS = ['Title Score', 'Tag Score', 'Desc Score',
  'Mockup Score', 'Overall Score', 'Views', 'Favorites', 'Sales 30d',
  'Revenue 30d', 'Profit 30d', 'Orientation', 'Primary Color',
  'Secondary Color', 'Aspect Ratio', 'Tags', 'Price', 'Home Style',
  'Room', 'Subject', 'Who Made', 'Category', 'Shop Section'];

// Columns editable in edit mode (v1: controlled-vocab attributes). Maps the
// grid column → the backend attribute field + which option list to use.
const EDITABLE_ATTR_COLS = {
  'Orientation': { field: 'orientation', opt: 'orientations' },
  'Primary Color': { field: 'primary_color', opt: 'colors' },
  'Secondary Color': { field: 'secondary_color', opt: 'colors' },
};
const cap1 = (s) => (typeof s === 'string' && s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// Multi-value editable columns (chip editor in edit mode). Tags = free-text
// chips for THIS listing only (no vocab list). Room/Subject/Home Style =
// full controlled-vocab checkboxes (from /api/optimizer/attribute-options).
const EDITABLE_MULTI_COLS = {
  'Tags': { field: 'tags', vocab: null },
  'Room': { field: 'rooms', vocab: 'rooms' },
  'Subject': { field: 'subjects', vocab: 'subjects' },
  'Home Style': { field: 'home_styles', vocab: 'home_styles' },
};

// Most slices a donut, or series a Z breakdown, draws before the smallest
// fold into "Other". Past ~10 the colours repeat and the legend stops
// being readable, whatever space it gets.
const MAX_CHART_CATEGORIES = 10;
// Tallest the chart legend grows (px) before it scrolls instead.
const CHART_LEGEND_MAX_H = 64;

const colLetter = (i) => String.fromCharCode(65 + i);

// Why the AI proxy refused a question, in words a seller can act on. The
// 402 body is extras_routes.charge_or_402's: it carries the free plan's
// allowance status (hosted/allowance.py) so we can say WHY a question
// wasn't free instead of a bare "out of credits".
async function refusalMessage(response) {
  if (response.status === 401) {
    return 'Sign in to ask the AI. A free account includes questions every day, no credit card.';
  }
  const j = await response.json().catch(() => ({}));
  if (response.status === 402) {
    const a = j.allowance || {};
    if (a.reason === 'verify_email') return 'Confirm your email to switch on your free daily questions.';
    if (a.reason === 'connect_shop') return 'Connect your Etsy shop to switch on your free daily questions.';
    if (a.reason === 'shop_shared') return 'This shop\'s free questions belong to the account that connected it first. Add credits to keep asking.';
    if (a.eligible && a.analyzer_left === 0) {
      return 'You\'ve used today\'s free questions. They reset at midnight UTC, or add credits to keep asking.';
    }
    if (a.eligible) {
      return 'That question is too big for a free one. Filter the grid down, or add credits.';
    }
  }
  return j.message || j.error || 'AI request refused. Try again later.';
}

// Theme palettes: light (ListEZ "Light Studio" cream) and dark (deep charcoal).
// LIGHT mirrors the site's --ls-* design tokens (see CLAUDE.md § Design-Token
// Discipline) so the widget matches the surrounding app shell instead of the
// generic Excel-green theme it was ported with. Token name noted per key so
// any future token change is easy to trace back here.
const LIGHT = {
  bg: '#F7F2E8',          // --ls-bg  (warm cream page background)
  card: '#FDFAF3',        // --ls-bg2 (card / sheet surface)
  cardAlt: '#F3ECDD',     // between bg2 and bg3 (zebra / alt rows)
  hover: '#F1EADB',       // subtle warm row hover
  border: 'rgba(92,72,42,0.20)',   // --ls-border, slightly stronger for cell grid
  borderLight: 'rgba(92,72,42,0.11)', // --ls-border (hairline)
  headerBg: '#EFE8D8',    // --ls-bg3 (column header strip)
  headerBg2: '#E7DECB',   // slightly deeper header accent
  text: '#2A2218',        // --ls-text
  text2: '#6B5E48',       // --ls-text2
  text3: '#9A8D70',       // --ls-text3
  text4: '#B6AB8F',       // muted-est
  accent: '#8A3A1A',      // --ls-accent (terracotta) — replaces Excel green
  accentDark: '#6E2D12',  // darker terracotta for text-on-light
  accentLight: '#EFE1D3', // warm terracotta tint (group/selection backgrounds)
  ownerCellOpen: '#F3ECDD',
  inputBg: '#FDFAF3',
  // status pill backgrounds (warm-retuned to --ls-green / --ls-gold / --ls-red)
  pillCompleteBg: '#E2EDE4', pillCompleteFg: '#2E6340',   // --ls-green family
  pillProgressBg: '#F4EACF', pillProgressFg: '#8A6B1F',   // --ls-gold family
  pillNotStartedBg: '#ECE4D4', pillNotStartedFg: '#6B5E48',
  pillBlockedBg: '#F2E0DB', pillBlockedFg: '#8A2A2A',     // --ls-red family
  errorBg: '#F7E7E2', errorBorder: '#8A2A2A', errorFg: '#8A2A2A',
};
const DARK = {
  bg: '#0f1014',
  card: '#1a1c20',
  cardAlt: '#15161a',
  hover: '#22252b',
  border: '#2a2d33',
  borderLight: '#23262b',
  headerBg: '#1f2126',
  headerBg2: '#232529',
  text: '#e8e8e0',
  text2: '#a8a8a0',
  text3: '#888880',
  text4: '#6a6a62',
  accent: '#26a55c',
  accentDark: '#178545',
  accentLight: '#1e3a2a',
  ownerCellOpen: '#1c2520',
  inputBg: '#15161a',
  pillCompleteBg: '#1e3a2a', pillCompleteFg: '#7ac99a',
  pillProgressBg: '#3a2f15', pillProgressFg: '#e8c060',
  pillNotStartedBg: '#25272b', pillNotStartedFg: '#9a9a92',
  pillBlockedBg: '#3a1e1a', pillBlockedFg: '#e88472',
  errorBg: '#2a1815', errorBorder: '#d4452a', errorFg: '#e88472',
};

export default function AISpreadsheet() {
  const [headers, setHeaders] = useState(INITIAL_HEADERS);
  const [data, setData] = useState(INITIAL_DATA);

  // ── ListEZ integration (Phase 1b) ────────────────────────────
  // isAnonymous: backend signals via /api/spreadsheet/data response.
  // Used to grey out AI buttons + redirect clicks to signup.
  // initialLoadDone: true after first fetch returns (success or fail).
  const [isAnonymous, setIsAnonymous] = useState(true);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  // Daily trend mode: when on, fetch one row per (pin, day) with a Date
  // column so the chart can draw real trendlines. Off = default per-pin grid.
  const [dailyView, setDailyView] = useState(false);
  const DAILY_DAYS = 30;
  // Windowed rendering: only the first `renderLimit` visible rows become
  // DOM <tr>s. Charts, insights, filters and the status bar still use the
  // FULL visible set — this caps DOM size only. Extends on scroll-near-
  // bottom or via the "show more" row. Without this, daily-trend mode
  // (one row per pin per day = pins × days) rendered tens of thousands of
  // rows and froze the tab.
  const ROW_RENDER_CHUNK = 250;
  const [renderLimit, setRenderLimit] = useState(ROW_RENDER_CHUNK);
  // Data scope: 'both' (per-pin, all cols) | 'etsy' (one row per listing,
  // pin cols hidden) | 'pinterest' (per-pin, listing-level cols hidden).
  const [scope, setScope] = useState('both');
  // Saved chart presets (up to 5): [{id, name, config:{chartType,chartX,
  // chartY,chartZ,chartFilters,yAggregations}}]. Persisted in settings.
  const [chartPresets, setChartPresets] = useState([]);
  const [presetsMenuOpen, setPresetsMenuOpen] = useState(false);
  const MAX_PRESETS = 5;
  // Edit mode (v1): edit controlled-vocab attributes, save to ListEZ.
  // pendingEdits keyed by Listing Key → { field: value }. attrOptions is
  // the controlled vocab fetched from /api/optimizer/attribute-options.
  const [editMode, setEditMode] = useState(false);
  const [pendingEdits, setPendingEdits] = useState({});
  const [attrOptions, setAttrOptions] = useState(null);
  const [multiEditor, setMultiEditor] = useState(null); // `${rowId}:${col}` open editor
  const [tagInput, setTagInput] = useState('');
  // Column visibility: names in this set are hidden from the grid (data
  // for them is still fetched + usable in charts). Lets the grid carry
  // lots of columns without showing them all at once.
  const [hiddenCols, setHiddenCols] = useState(new Set(DEFAULT_HIDDEN_COLS));
  const [columnsMenuOpen, setColumnsMenuOpen] = useState(false);

  // Fetch live data from the Flask backend on mount. Falls back to the
  // empty INITIAL_HEADERS/INITIAL_DATA if the call fails so the table
  // doesn't crash — the user sees an empty grid with the error banner.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const url = dailyView
          ? `/api/spreadsheet/data?view=daily&days=${DAILY_DAYS}`
          : '/api/spreadsheet/data';
        const r = await fetch(url);
        if (!r.ok) throw new Error('HTTP ' + r.status);
        const d = await r.json();
        if (cancelled) return;
        if (d.ok) {
          if (Array.isArray(d.columns) && d.columns.length) setHeaders(d.columns);
          if (Array.isArray(d.rows)) setData(d.rows);
          setIsAnonymous(Boolean(d.is_anonymous));
        }
      } catch (e) {
        if (!cancelled) setError('Could not load your data: ' + (e.message || e));
      } finally {
        if (!cancelled) setInitialLoadDone(true);
      }
    })();
    return () => { cancelled = true; };
  }, [dailyView]);

  const [filterIds, setFilterIds] = useState(null);
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [draggedIdx, setDraggedIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [aiCommand, setAiCommand] = useState('');
  const [aiExplanation, setAiExplanation] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [colFilters, setColFilters] = useState({}); // { colName: [allowedValues] }
  // Numeric condition filters for value columns (counts, scores, CTR %),
  // e.g. Impressions > 0. { colName: { op: '>'|'>='|'<'|'<='|'='|'!=',
  // value: '<string from the input>' } }. Kept separate from colFilters —
  // discrete checkbox lists make no sense on columns with thousands of
  // distinct numbers.
  const [numFilters, setNumFilters] = useState({});
  const [openFilter, setOpenFilter] = useState(null); // column name or null
  // Bounding rect of the trigger that opened the current menu. Menus render
  // position:fixed off this so they escape the grid/chart overflow clipping
  // (was: absolute inside sheetWrap/chartWrap → bottom items unreachable +
  // right-edge menus cut off). Only one menu is open at a time.
  const [menuRect, setMenuRect] = useState(null);
  const [ownerEditor, setOwnerEditor] = useState(null); // rowId or null
  const [newOwnerInput, setNewOwnerInput] = useState('');
  const [selectedCell, setSelectedCell] = useState(null); // {rowId, col}
  const [fillDrag, setFillDrag] = useState(null); // {col, sourceRowId, targetRowId}
  const [justFilledIds, setJustFilledIds] = useState([]); // brief highlight after fill
  const [groupBy, setGroupBy] = useState([]); // array of column names to group by

  // Rewind the render window whenever the row set or its order changes,
  // so the user starts at the top of the fresh result.
  useEffect(() => {
    setRenderLimit(ROW_RENDER_CHUNK);
  }, [dailyView, scope, filterIds, colFilters, numFilters, sortCol, sortDir, groupBy]);
  const [collapsedGroups, setCollapsedGroups] = useState(new Set()); // group keys that are collapsed
  const [groupBarDragOver, setGroupBarDragOver] = useState(false);
  const [chartX, setChartX] = useState([]); // array of column names
  const [chartY, setChartY] = useState([]); // array of column names
  const [chartZ, setChartZ] = useState(null); // optional single column
  const [chartType, setChartType] = useState('bar');
  const [chartDragX, setChartDragX] = useState(false);
  const [chartDragY, setChartDragY] = useState(false);
  const [chartDragZ, setChartDragZ] = useState(false);
  const [chartFilters, setChartFilters] = useState({}); // { col: [allowedValues] }
  const [chartFilterDragOver, setChartFilterDragOver] = useState(false);
  const [openChartFilter, setOpenChartFilter] = useState(null);
  const [yAggregations, setYAggregations] = useState({}); // { col: 'sum'|'avg'|'max'|'min'|'count' }
  const [openYAggMenu, setOpenYAggMenu] = useState(null);
  const [colDragging, setColDragging] = useState(false);
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [heatMapOn, setHeatMapOn] = useState(true);
  const [darkMode, setDarkMode] = useState(false);

  // ── Saved settings — load + auto-save (Phase 2) ───────────────
  // Anonymous: settings persist in localStorage (per-device).
  // Logged in: settings persist via /api/spreadsheet/settings (per-user,
  //   follows them across devices). localStorage acts as an instant
  //   client-side fallback while the backend round-trips on first load.
  // Auto-save is debounced 600ms so a flurry of axis drags doesn't
  // spam the backend.
  const LS_SETTINGS_KEY = 'ezAnalyzer:settings:v1';

  // Flask-WTF requires X-CSRFToken on every POST. Token sits in the
  // csrf_token cookie that Flask injects on logged-in HTML responses
  // (see hosted/server.py _inject_csrf_token). Same pattern as
  // static/ezrank.js. Returns the header object to spread into fetch.
  const csrfHeaders = () => {
    const m = document.cookie.match(/csrf_token=([^;]+)/);
    return m ? { 'X-CSRFToken': m[1] } : {};
  };
  const settingsLoadedRef = useRef(false);
  const saveDebounceRef = useRef(null);

  // Load once when the data fetch resolves (so we know isAnonymous).
  useEffect(() => {
    if (!initialLoadDone) return;
    if (settingsLoadedRef.current) return;
    settingsLoadedRef.current = true;
    (async () => {
      let s = null;
      // 1. Try localStorage first — instant, no network round-trip.
      try {
        const cached = localStorage.getItem(LS_SETTINGS_KEY);
        if (cached) s = JSON.parse(cached);
      } catch (_) { /* corrupt JSON, ignore */ }
      // 2. If logged in, prefer the backend value (cross-device).
      if (!isAnonymous) {
        try {
          const r = await fetch('/api/spreadsheet/settings');
          if (r.ok) {
            const d = await r.json();
            if (d && d.ok && d.settings && Object.keys(d.settings).length) {
              s = d.settings;
            }
          }
        } catch (_) { /* offline, fall back to localStorage */ }
      }
      if (!s || typeof s !== 'object') return;
      // Apply each saved key to the matching setter. Missing keys
      // leave the default state in place — never blow up if the
      // shape is unfamiliar (forward-compat with future settings).
      if (Array.isArray(s.chartX))         setChartX(s.chartX);
      if (Array.isArray(s.chartY))         setChartY(s.chartY);
      if (s.chartZ !== undefined)          setChartZ(s.chartZ);
      if (typeof s.chartType === 'string') setChartType(s.chartType);
      if (s.chartFilters && typeof s.chartFilters === 'object') setChartFilters(s.chartFilters);
      if (s.yAggregations && typeof s.yAggregations === 'object') setYAggregations(s.yAggregations);
      if (Array.isArray(s.groupBy))        setGroupBy(s.groupBy);
      if (Array.isArray(s.collapsedGroups)) setCollapsedGroups(new Set(s.collapsedGroups));
      if (s.colFilters && typeof s.colFilters === 'object') setColFilters(s.colFilters);
      if (s.numFilters && typeof s.numFilters === 'object') setNumFilters(s.numFilters);
      if (Array.isArray(s.hiddenCols)) {
        const known = new Set(Array.isArray(s.knownDefaultHidden)
          ? s.knownDefaultHidden : LEGACY_KNOWN_DEFAULT_HIDDEN);
        setHiddenCols(new Set([...s.hiddenCols, ...DEFAULT_HIDDEN_COLS.filter((c) => !known.has(c))]));
      }
      if (typeof s.scope === 'string')     setScope(s.scope);
      if (Array.isArray(s.chartPresets))   setChartPresets(s.chartPresets);
      if (s.sortCol !== undefined)         setSortCol(s.sortCol);
      if (typeof s.sortDir === 'string')   setSortDir(s.sortDir);
      if (typeof s.theme === 'string')     setDarkMode(s.theme === 'dark');
    })();
  }, [initialLoadDone, isAnonymous]);

  // Auto-save on debounced change. Watches every persistent state
  // value. settingsLoadedRef.current gates this so the very first
  // load-then-render doesn't trigger a save that would just overwrite
  // the freshly-loaded values with defaults (race condition).
  useEffect(() => {
    if (!settingsLoadedRef.current) return;
    // Daily-trend mode clears chart/group/filter state for its own columns.
    // Do NOT persist while in daily mode, or that transient clear would
    // overwrite the user's saved chart config. The normal-mode config stays
    // intact in localStorage/backend and is restored when daily mode is left.
    if (dailyView) return;
    // Cancel any in-flight save and queue a fresh one 600ms out.
    if (saveDebounceRef.current) clearTimeout(saveDebounceRef.current);
    saveDebounceRef.current = setTimeout(() => {
      const settings = {
        chartX, chartY, chartZ, chartType, chartFilters, yAggregations,
        groupBy,
        collapsedGroups: Array.from(collapsedGroups || []),
        colFilters,
        numFilters,
        hiddenCols: Array.from(hiddenCols || []),
        knownDefaultHidden: DEFAULT_HIDDEN_COLS,
        scope,
        chartPresets,
        sortCol, sortDir,
        theme: darkMode ? 'dark' : 'light',
      };
      // localStorage always, instant.
      try {
        localStorage.setItem(LS_SETTINGS_KEY, JSON.stringify(settings));
      } catch (_) { /* quota or disabled — silent */ }
      // Backend only for logged-in users.
      if (!isAnonymous) {
        fetch('/api/spreadsheet/settings', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...csrfHeaders() },
          body: JSON.stringify(settings),
        }).catch(() => { /* network blip — localStorage holds the value */ });
      }
    }, 600);
    return () => {
      if (saveDebounceRef.current) clearTimeout(saveDebounceRef.current);
    };
  }, [
    isAnonymous,
    chartX, chartY, chartZ, chartType,
    chartFilters, yAggregations,
    groupBy, collapsedGroups,
    colFilters, numFilters, hiddenCols, scope, chartPresets, sortCol, sortDir,
    darkMode, dailyView,
  ]);
  // ──────────────────────────────────────────────────────────────
  const t = darkMode ? DARK : LIGHT;
  const ACCENT = t.accent;
  const ACCENT_DARK = t.accentDark;
  const ACCENT_LIGHT = t.accentLight;
  const inputRef = useRef(null);

  // Deep-copy a cell value (handles arrays for Owner column)
  const copyValue = (v) => (Array.isArray(v) ? [...v] : v);

  const completeFill = () => {
    if (!fillDrag) return;
    const { col, sourceRowId, targetRowId } = fillDrag;
    if (sourceRowId === targetRowId) { setFillDrag(null); return; }
    const sourceRow = data.find((r) => r._id === sourceRowId);
    if (!sourceRow) { setFillDrag(null); return; }
    const sourceValue = sourceRow[col];

    // Compute the visible range between source and target
    const sourceIdx = visible.findIndex((r) => r._id === sourceRowId);
    const targetIdx = visible.findIndex((r) => r._id === targetRowId);
    if (sourceIdx === -1 || targetIdx === -1) { setFillDrag(null); return; }
    const lo = Math.min(sourceIdx, targetIdx);
    const hi = Math.max(sourceIdx, targetIdx);
    const targetIds = visible.slice(lo, hi + 1).map((r) => r._id).filter((id) => id !== sourceRowId);

    setData((prev) => prev.map((r) => {
      if (targetIds.includes(r._id)) return { ...r, [col]: copyValue(sourceValue) };
      return r;
    }));
    setJustFilledIds(targetIds);
    setTimeout(() => setJustFilledIds([]), 600);
    setFillDrag(null);
  };

  // Global mouse-up handler while filling
  useEffect(() => {
    if (!fillDrag) return;
    const handler = () => completeFill();
    window.addEventListener('mouseup', handler);
    return () => window.removeEventListener('mouseup', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fillDrag]);

  // Owner helpers — Owner is normalized as an array of names
  const ownersOf = (v) => {
    if (Array.isArray(v)) return v;
    if (v == null || v === '') return [];
    if (typeof v === 'string') return [v];
    return [String(v)];
  };
  const ownerInitials = (name) => {
    const parts = String(name).trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return (parts[0] || '?').slice(0, 2).toUpperCase();
  };
  const ownerColor = (name) => {
    let h = 0;
    for (let i = 0; i < String(name).length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xfffffff;
    const hue = h % 360;
    return darkMode ? `hsl(${hue}, 45%, 48%)` : `hsl(${hue}, 50%, 58%)`;
  };
  const allOwners = () => {
    const set = new Set();
    data.forEach((r) => ownersOf(r.Owner).forEach((o) => set.add(o)));
    return [...set];
  };
  const toggleOwnerForRow = (rowId, owner) => {
    setData((prev) => prev.map((r) => {
      if (r._id !== rowId) return r;
      const current = ownersOf(r.Owner);
      const next = current.includes(owner)
        ? current.filter((o) => o !== owner)
        : [...current, owner];
      return { ...r, Owner: next };
    }));
  };
  const addOwnerToRow = (rowId) => {
    const name = newOwnerInput.trim();
    if (!name) return;
    setData((prev) => prev.map((r) => {
      if (r._id !== rowId) return r;
      const current = ownersOf(r.Owner);
      if (current.includes(name)) return r;
      return { ...r, Owner: [...current, name] };
    }));
    setNewOwnerInput('');
  };

  // Helpers for column filters
  const uniqueValues = (col) => {
    const seen = new Set();
    data.forEach((r) => {
      const v = r[col];
      if (Array.isArray(v)) v.forEach((x) => seen.add(x));
      else seen.add(v);
    });
    return [...seen];
  };
  const isColFiltered = (col) => colFilters[col] !== undefined;
  const isValueChecked = (col, value) => {
    if (!isColFiltered(col)) return true;
    return colFilters[col].includes(value);
  };
  const toggleColValue = (col, value) => {
    const current = colFilters[col] ?? uniqueValues(col);
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    const all = uniqueValues(col);
    if (next.length === all.length && all.every((v) => next.includes(v))) {
      const { [col]: _, ...rest } = colFilters;
      setColFilters(rest);
    } else {
      setColFilters({ ...colFilters, [col]: next });
    }
  };
  const selectAllForCol = (col) => {
    const { [col]: _, ...rest } = colFilters;
    setColFilters(rest);
  };
  const clearForCol = (col) => {
    setColFilters({ ...colFilters, [col]: [] });
  };

  // ── Numeric condition filters ────────────────────────────────
  // Blank rows (null/undefined/'') never satisfy a numeric condition —
  // "Impressions > 0" should not surface pins that have no data at all.
  const NUM_OPS = ['>', '>=', '<', '<=', '=', '!='];
  const isNumFiltered = (col) => {
    const f = numFilters[col];
    return !!(f && f.value !== '' && f.value != null && !isNaN(Number(f.value)));
  };
  const setNumFilter = (col, patch) => {
    setNumFilters((prev) => ({
      ...prev,
      [col]: { op: '>', value: '', ...(prev[col] || {}), ...patch },
    }));
  };
  const clearNumFilter = (col) => {
    setNumFilters((prev) => {
      const { [col]: _, ...rest } = prev;
      return rest;
    });
  };
  const numMatches = (raw, f) => {
    if (raw == null || raw === '') return false;
    const n = typeof raw === 'number' ? raw : Number(raw);
    if (isNaN(n)) return false;
    const target = Number(f.value);
    switch (f.op) {
      case '>':  return n >  target;
      case '>=': return n >= target;
      case '<':  return n <  target;
      case '<=': return n <= target;
      case '=':  return n === target;
      case '!=': return n !== target;
      default:   return true;
    }
  };

  // Data scope (Both / Etsy / Pinterest). Etsy collapses to ONE row per
  // listing — the listing-level fields are identical across a listing's
  // pins, so we keep the first and re-key its _id. Skipped in daily mode
  // (rows there are per-pin-per-day). Pinterest/Both stay per-pin.
  const scopedData = (scope === 'etsy' && !dailyView)
    ? Array.from(
        data.reduce((m, r) => {
          const k = r['Listing Key'] != null ? r['Listing Key'] : r._id;
          if (!m.has(k)) m.set(k, { ...r, _id: 'L:' + k });
          return m;
        }, new Map()).values()
      )
    : data;

  // Compute visible data: AI filter → column filters → sort
  let visible = filterIds === null ? scopedData : scopedData.filter((r) => filterIds.includes(r._id));
  Object.entries(numFilters).forEach(([col, f]) => {
    if (!isNumFiltered(col)) return;
    visible = visible.filter((r) => numMatches(r[col], f));
  });
  Object.entries(colFilters).forEach(([col, allowed]) => {
    visible = visible.filter((r) => {
      const val = r[col];
      if (Array.isArray(val)) return val.some((v) => allowed.includes(v));
      return allowed.includes(val);
    });
  });
  if (sortCol) {
    visible = [...visible].sort((a, b) => {
      let av = a[sortCol], bv = b[sortCol];
      if (Array.isArray(av)) av = av.join(', ');
      if (Array.isArray(bv)) bv = bv.join(', ');
      if (av == null) return 1;
      if (bv == null) return -1;
      const isNum = typeof av === 'number' && typeof bv === 'number';
      let cmp = isNum ? av - bv : String(av).localeCompare(String(bv));
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }

  // Build flat render list when grouping is active
  const formatGroupValue = (v) => {
    if (Array.isArray(v)) return v.join(', ');
    if (v == null || v === '') return '(empty)';
    return String(v);
  };

  const buildGroupedRows = (rows, depth = 0, parentKey = '') => {
    if (depth >= groupBy.length) {
      return rows.map((r) => ({ type: 'data', row: r, depth }));
    }
    const col = groupBy[depth];
    const groups = new Map();
    rows.forEach((r) => {
      const key = formatGroupValue(r[col]);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(r);
    });
    const result = [];
    groups.forEach((groupRows, key) => {
      const fullKey = parentKey + '|' + col + '=' + key;
      const isCollapsed = collapsedGroups.has(fullKey);
      const aggregates = {};
      headers.forEach((h) => {
        if (data.length > 0 && typeof data[0][h] === 'number') {
          aggregates[h] = groupRows.reduce((s, r) => s + (typeof r[h] === 'number' ? r[h] : 0), 0);
        }
      });
      result.push({
        type: 'group',
        key: fullKey,
        depth,
        col,
        value: key,
        count: groupRows.length,
        aggregates,
        collapsed: isCollapsed,
      });
      if (!isCollapsed) {
        result.push(...buildGroupedRows(groupRows, depth + 1, fullKey));
      }
    });
    return result;
  };

  const groupedRows = groupBy.length > 0 ? buildGroupedRows(visible) : null;

  // O(1) row-identity lookups for the render path. The old findIndex-per-
  // row made rendering O(rows²) — with daily-mode row counts (pins × days)
  // that alone could burn seconds per re-render.
  const idToDataIdx = new Map();
  data.forEach((r, i) => idToDataIdx.set(r._id, i));
  const idToVisIdx = new Map();
  visible.forEach((r, i) => idToVisIdx.set(r._id, i));

  // Windowed rendering support: total renderable rows (data rows, or the
  // flattened group list) and a scroll handler that extends the window
  // when the user nears the bottom of the sheet.
  const totalRenderRows = groupedRows ? groupedRows.length : visible.length;
  const onSheetScroll = (e) => {
    if (renderLimit >= totalRenderRows) return;
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 400) {
      setRenderLimit((l) => Math.min(l + ROW_RENDER_CHUNK, totalRenderRows));
    }
  };

  const toggleGroupCollapse = (key) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const addGroupBy = (col) => {
    if (!col || groupBy.includes(col) || !headers.includes(col)) return;
    setGroupBy([...groupBy, col]);
  };

  const removeGroupBy = (col) => {
    setGroupBy(groupBy.filter((c) => c !== col));
  };

  // Compute chart data
  const isNumericColName = (col) => data.length > 0 && typeof data[0][col] === 'number';
  const SERIES_COLORS = [ACCENT, '#d4452a', '#e8a020', '#3a7ca5', '#7a4e8a', '#5c7c4d', '#8a5a2a', '#a13a25', '#4a8a8a', '#c45a8e'];
  const seriesLabel = (yf) => (isNumericColName(yf) ? `Sum of ${yf}` : `Count of ${yf}`);

  // For bar/line with Z: Z values become the series; Y[0] is the measure.
  // Compute unique Z values from visible data.
  const zValues = chartZ
    ? Array.from(new Set(visible.map((r) => formatGroupValue(r[chartZ]))))
    : [];

  const chartTitleText = (() => {
    if (chartX.length === 0 || chartY.length === 0) return '';
    const ys = chartZ
      ? `${seriesLabel(chartY[0])} broken down by ${chartZ}`
      : chartY.map(seriesLabel).join(', ');
    const xs = chartX.join(' × ');
    return `${ys} by ${xs}`;
  })();

  const rawChartData = (() => {
    if (chartX.length === 0 || chartY.length === 0) return [];
    // Apply chart-specific filters before aggregation
    let chartRows = visible;
    Object.entries(chartFilters).forEach(([col, allowed]) => {
      if (allowed && allowed.length >= 0) {
        chartRows = chartRows.filter((r) => {
          const val = r[col];
          if (Array.isArray(val)) return val.some((v) => allowed.includes(v));
          return allowed.includes(val);
        });
      }
    });

    // Determine per-Y aggregation function (sum default for numeric, count for non-numeric)
    const aggFor = (yf) => yAggregations[yf] || (isNumericColName(yf) ? 'sum' : 'count');
    const initial = (agg) => (agg === 'max' ? -Infinity : agg === 'min' ? Infinity : 0);
    const apply = (current, val, agg, count) => {
      const n = typeof val === 'number' ? val : 0;
      if (agg === 'sum') return current + n;
      if (agg === 'count') return count;
      if (agg === 'avg') return current + n;
      if (agg === 'max') return Math.max(current, n);
      if (agg === 'min') return Math.min(current, n);
      return current;
    };

    if (chartType === 'scatter') {
      const xf = chartX[0], yf = chartY[0];
      return chartRows
        .map((row) => ({
          x: typeof row[xf] === 'number' ? row[xf] : Number(row[xf]),
          y: typeof row[yf] === 'number' ? row[yf] : Number(row[yf]),
          z: chartZ ? (typeof row[chartZ] === 'number' ? row[chartZ] : Number(row[chartZ]) || 1) : 1,
          label: row.Task || row._id,
        }))
        .filter((d) => !isNaN(d.x) && !isNaN(d.y));
    }

    // Bar / Line aggregation
    const map = new Map();
    const countMap = new Map(); // for avg calc
    if (chartZ) {
      const yMeasure = chartY[0];
      const agg = aggFor(yMeasure);
      chartRows.forEach((row) => {
        const xKey = chartX.map((f) => formatGroupValue(row[f])).join(' | ');
        if (!map.has(xKey)) {
          const entry = { x: xKey };
          zValues.forEach((zv) => { entry[zv] = initial(agg); });
          map.set(xKey, entry);
          countMap.set(xKey, {});
        }
        const entry = map.get(xKey);
        const cnts = countMap.get(xKey);
        const zVal = formatGroupValue(row[chartZ]);
        cnts[zVal] = (cnts[zVal] || 0) + 1;
        entry[zVal] = apply(entry[zVal] || initial(agg), row[yMeasure], agg, cnts[zVal]);
      });
      // For avg, divide by count
      if (agg === 'avg') {
        map.forEach((entry, xKey) => {
          const cnts = countMap.get(xKey);
          zValues.forEach((zv) => { if (cnts[zv]) entry[zv] = entry[zv] / cnts[zv]; });
        });
      }
    } else {
      chartRows.forEach((row) => {
        const xKey = chartX.map((f) => formatGroupValue(row[f])).join(' | ');
        if (!map.has(xKey)) {
          const entry = { x: xKey };
          chartY.forEach((yf) => { entry[yf] = initial(aggFor(yf)); });
          map.set(xKey, entry);
          countMap.set(xKey, 0);
        }
        const entry = map.get(xKey);
        countMap.set(xKey, countMap.get(xKey) + 1);
        chartY.forEach((yf) => {
          entry[yf] = apply(entry[yf], row[yf], aggFor(yf), countMap.get(xKey));
        });
      });
      // For avg, divide by per-x count
      chartY.forEach((yf) => {
        if (aggFor(yf) === 'avg') {
          map.forEach((entry, xKey) => { entry[yf] = entry[yf] / countMap.get(xKey); });
        }
      });
    }
    return Array.from(map.values());
  })();

  // One legend for every chart type, held to a fixed height. Recharts'
  // stock legend grows with its entries and is positioned inside the chart
  // wrapper, so a long one spilled up over the X/Y/Z drop zones and buried
  // their remove buttons. This one wraps, scrolls past CHART_LEGEND_MAX_H,
  // and ellipsises each entry (full text in the tooltip). Called inline —
  // not as a component — so Recharts still finds a <Legend> child.
  const chartLegend = (format) => (
    <Legend
      verticalAlign="bottom"
      content={({ payload }) => (
        <div style={styles.chartLegend}>
          {(payload || []).map((entry, i) => {
            const label = String(format(entry.value));
            return (
              <span key={`${entry.value}-${i}`} style={styles.chartLegendItem} title={label}>
                <span style={{ ...styles.chartLegendSwatch, background: entry.color }} />
                <span style={styles.chartLegendText}>{label}</span>
              </span>
            );
          })}
        </div>
      )}
    />
  );

  // Readability cap. A donut slice or a Z series per value only reads while
  // there are a handful of them: drop "Listing" on a donut, or on Z, and the
  // chart grows 100+ slices/series whose legend no container can hold. Keep
  // the MAX_CHART_CATEGORIES-1 largest and fold the rest into one "Other"
  // bucket. Folding is only honest for additive aggregations (sum, count) —
  // an "Other" of averages or maxima would be a made-up number — so for
  // avg/min/max the tail is dropped instead, and `chartCapNote` says so.
  const { chartData, chartSeriesKeys, chartCapNote, chartOtherKey } = (() => {
    const aggOf = (yf) => yAggregations[yf] || (isNumericColName(yf) ? 'sum' : 'count');
    const additive = (yf) => ['sum', 'count'].includes(aggOf(yf));
    const baseKeys = chartZ ? zValues : chartY;
    const num = (v) => (typeof v === 'number' && isFinite(v) ? v : 0);

    if (chartType === 'donut' && rawChartData.length > MAX_CHART_CATEGORIES) {
      const measure = baseKeys[0];
      const sorted = [...rawChartData].sort((a, b) => num(b[measure]) - num(a[measure]));
      const keep = sorted.slice(0, MAX_CHART_CATEGORIES - 1);
      const rest = sorted.slice(MAX_CHART_CATEGORIES - 1);
      if (additive(measure)) {
        const otherLabel = `Other (${rest.length})`;
        keep.push({ x: otherLabel, [measure]: rest.reduce((t, r) => t + num(r[measure]), 0) });
        return { chartData: keep, chartSeriesKeys: baseKeys, chartOtherKey: otherLabel,
          chartCapNote: `Top ${MAX_CHART_CATEGORIES - 1} of ${rawChartData.length} shown; the rest are grouped as Other` };
      }
      return { chartData: keep, chartSeriesKeys: baseKeys,
        chartCapNote: `Top ${keep.length} of ${rawChartData.length} shown` };
    }

    if (chartZ && chartType !== 'scatter' && zValues.length > MAX_CHART_CATEGORIES) {
      const totals = new Map(zValues.map((zv) => [zv, 0]));
      rawChartData.forEach((entry) => zValues.forEach((zv) => totals.set(zv, totals.get(zv) + num(entry[zv]))));
      const ranked = [...zValues].sort((a, b) => totals.get(b) - totals.get(a));
      const keep = ranked.slice(0, MAX_CHART_CATEGORIES - 1);
      const rest = ranked.slice(MAX_CHART_CATEGORIES - 1);
      const fold = additive(chartY[0]);
      const otherKey = `Other (${rest.length})`;
      const rows = rawChartData.map((entry) => {
        const out = { x: entry.x };
        keep.forEach((zv) => { out[zv] = entry[zv]; });
        if (fold) out[otherKey] = rest.reduce((t, zv) => t + num(entry[zv]), 0);
        return out;
      });
      return { chartData: rows, chartSeriesKeys: fold ? [...keep, otherKey] : keep,
        chartOtherKey: fold ? otherKey : null,
        chartCapNote: fold
          ? `Top ${keep.length} of ${zValues.length} ${chartZ} values shown; the rest are grouped as Other`
          : `Top ${keep.length} of ${zValues.length} ${chartZ} values shown` };
    }

    return { chartData: rawChartData, chartSeriesKeys: baseKeys, chartCapNote: '', chartOtherKey: null };
  })();
  // Palette colour for series/slice i; the folded "Other" bucket is drawn
  // neutral so it never reads as one more listing.
  const seriesColor = (key, i) => (key != null && key === chartOtherKey
    ? t.text4 : SERIES_COLORS[i % SERIES_COLORS.length]);

  // Drag handlers — operate on the underlying data array
  const onDragStart = (e, visIdx) => {
    if (sortCol) {
      e.preventDefault();
      return;
    }
    const row = visible[visIdx];
    const dataIdx = data.findIndex((r) => r._id === row._id);
    setDraggedIdx(dataIdx);
    e.dataTransfer.effectAllowed = 'move';
  };

  const onDragOver = (e, visIdx) => {
    e.preventDefault();
    if (draggedIdx === null) return;
    const row = visible[visIdx];
    const dataIdx = data.findIndex((r) => r._id === row._id);
    setDragOverIdx(dataIdx);
  };

  const onDrop = (e) => {
    e.preventDefault();
    if (draggedIdx === null || dragOverIdx === null || draggedIdx === dragOverIdx) {
      setDraggedIdx(null);
      setDragOverIdx(null);
      return;
    }
    const next = [...data];
    const [moved] = next.splice(draggedIdx, 1);
    next.splice(dragOverIdx, 0, moved);
    setData(next);
    setDraggedIdx(null);
    setDragOverIdx(null);
  };

  const onDragEnd = () => {
    setDraggedIdx(null);
    setDragOverIdx(null);
  };

  const toggleSort = (col) => {
    if (sortCol !== col) { setSortCol(col); setSortDir('asc'); return; }
    if (sortDir === 'asc') { setSortDir('desc'); return; }
    setSortCol(null);
  };

  const startEdit = (rowId, col, currentVal) => {
    setEditing({ rowId, col });
    setEditValue(String(currentVal ?? ''));
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const commitEdit = () => {
    if (!editing) return;
    setData((prev) => prev.map((r) => {
      if (r._id !== editing.rowId) return r;
      const sample = data[0]?.[editing.col];
      const isNumeric = typeof sample === 'number';
      const v = isNumeric && editValue !== '' ? Number(editValue) : editValue;
      return { ...r, [editing.col]: isNumeric && isNaN(v) ? editValue : v };
    }));
    setEditing(null);
    setEditValue('');
  };

  const reset = () => {
    // Reset view state only — never wipe the loaded grid (headers/data come
    // from /api/spreadsheet/data; clearing them would blank the sheet with no
    // refetch and no recovery short of a full reload).
    setFilterIds(null);
    setColFilters({});
    setNumFilters({});
    setOpenFilter(null);
    setSortCol(null);
    setSortDir('asc');
    setAiExplanation('');
    setAiCommand('');
    setError('');
    setSelectedCell(null);
    setFillDrag(null);
    setGroupBy([]);
    setCollapsedGroups(new Set());
    setChartX([]);
    setChartY([]);
    setChartZ(null);
    setChartFilters({});
    setYAggregations({});
    setInsights(null);
  };

  const runAI = async (commandOverride) => {
    const cmd = (typeof commandOverride === 'string' ? commandOverride : aiCommand).trim();
    if (!cmd) return;
    setLoading(true);
    setError('');
    setAiExplanation('');

    const prompt = `You manipulate spreadsheet data. The user gives a natural language command.
Respond with ONLY valid JSON — no markdown fences, no preamble, no commentary.

Schema:
{
  "action": "filter_sort" | "clear",
  "filterIds": [number] | null,
  "sortColumn": string | null,
  "sortDirection": "asc" | "desc",
  "explanation": string
}

Rules:
- "filter_sort": modify the existing data. filterIds is an array of _id values to keep (or null to keep all). sortColumn is a column name (or null). sortDirection is "asc" or "desc".
- "clear": reset all filters and sorts.
- Never fabricate or replace rows — you only filter and sort the user's real listing data.
- Always include "explanation" — one short sentence describing what you did.

Current headers: ${JSON.stringify(headers)}
Current data: ${JSON.stringify(data)}

User command: "${cmd}"`;

    try {
      // Route through the Flask proxy (LLMRouter + credit billing).
      // Returns 401 if the user isn't logged in — handled below as
      // a signup-required nudge instead of a hard error.
      const response = await fetch('/api/spreadsheet/ai-proxy', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...csrfHeaders() },
        body: JSON.stringify({
          mode: 'command',
          prompt: prompt,
          max_tokens: 2000,
        }),
      });
      if ([401, 402, 413, 429].includes(response.status)) {
        setError(await refusalMessage(response));
        return;
      }
      if (!response.ok) throw new Error('Claude could not process that command.');
      const dataResp = await response.json();
      if (!dataResp.ok) throw new Error(dataResp.error || 'AI request failed.');
      const text = (dataResp.text || '').trim();
      const cleaned = text.replace(/^```json\s*/i, '').replace(/```\s*$/, '').trim();
      const parsed = JSON.parse(cleaned);

      if (parsed.action === 'clear') {
        setFilterIds(null);
        setSortCol(null);
      } else if (parsed.action === 'filter_sort') {
        setFilterIds(parsed.filterIds ?? null);
        setSortCol(parsed.sortColumn ?? null);
        setSortDir(parsed.sortDirection || 'asc');
      }
      // NOTE: the generic template's "generate" action (replace the whole grid
      // with fabricated rows) is intentionally NOT handled here — the analyzer
      // shows the user's real Etsy/Pinterest data and must never overwrite it.
      setAiExplanation(parsed.explanation || '');
      setAiCommand('');
    } catch (err) {
      setError(err.message || 'Something went wrong interpreting that command.');
    } finally {
      setLoading(false);
    }
  };

  const analyzeData = async () => {
    if (insightsLoading) return;
    setInsightsLoading(true);
    setInsights(null);
    setError('');
    try {
      // Send compact data to keep token use modest
      const sample = visible.slice(0, 200);
      const prompt = `You are a data analyst. Analyze the spreadsheet below and return 4-6 short, sharp, *non-obvious* insights — patterns, outliers, correlations, or risks the user might miss. Each insight is ONE sentence for a non-technical reader.

Output format: ONE insight per line, plain text. No numbering, no bullets, no JSON, no preamble, no blank lines.

Headers: ${JSON.stringify(headers)}
Rows: ${JSON.stringify(sample)}`;
      const response = await fetch('/api/spreadsheet/ai-proxy', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...csrfHeaders() },
        body: JSON.stringify({
          mode: 'insights',
          prompt: prompt,
          max_tokens: 1500,
        }),
      });
      if ([401, 402, 413, 429].includes(response.status)) {
        setError(await refusalMessage(response));
        return;
      }
      if (!response.ok) throw new Error('Could not analyze the data right now.');
      const dataResp = await response.json();
      if (!dataResp.ok) throw new Error(dataResp.error || 'Analysis failed.');
      const text = (dataResp.text || '').trim();
      // Insights are free text, not structured data — parse as plain
      // lines, NOT JSON. Forcing strict JSON here was fragile: an
      // unescaped quote, a newline inside a string, or a truncated
      // response all threw "Unterminated string in JSON". Splitting on
      // newlines can't hit those failure modes. Strip any stray bullet/
      // number prefixes and code fences the model might add anyway.
      const lines = text
        .split('\n')
        .map((l) => l.replace(/^\s*(?:[-*•]|\d+[.)])\s*/, '').trim())
        .filter((l) => l.length > 0 && !/^```/.test(l));
      setInsights(lines);
    } catch (err) {
      setError(err.message || 'Analysis failed.');
    } finally {
      setInsightsLoading(false);
    }
  };

  const fmt = (val, col) => {
    if (val == null) return '';
    if (col === 'Cost' && typeof val === 'number') return '$' + val.toLocaleString();
    return String(val);
  };

  // A column is numeric if the first non-blank value in a sample is a
  // number. Sampling (rather than trusting row 0) matters because sparse
  // columns like Impressions/Profit are frequently null on the first row,
  // which used to misclassify them as text — giving them a useless
  // checkbox filter and left-aligned cells.
  const isNumericCol = (col) => {
    const n = Math.min(data.length, 50);
    for (let i = 0; i < n; i++) {
      const v = data[i][col];
      if (v == null || v === '') continue;
      return typeof v === 'number';
    }
    return false;
  };

  // Minimum width a column needs so its header renders intact: the longest
  // word (multi-word labels wrap, so only the longest word is unbreakable)
  // at ~7.5px/char for 13px bold Manrope, plus 24px cell padding, the 22px
  // filter button, and the 4px flex gap. Floored at the old 90px default.
  // With table-layout:fixed these values act as ratios, so this widens the
  // affected columns relative to the rest rather than forcing a scrollbar.
  const headerFallbackWidth = (col) => {
    const longestWord = String(col || '')
      .split(/\s+/)
      .reduce((m, w) => Math.max(m, w.length), 0);
    return Math.max(90, Math.round(longestWord * 7.5) + 50);
  };

  // Columns actually rendered = all headers minus the user-hidden set.
  // Every column-iterating render loop uses this so hide/show stays
  // perfectly aligned across the colgroup, header, and body.
  const scopeHidden = dailyView ? []
    : scope === 'etsy' ? PIN_COLS
    : scope === 'pinterest' ? LISTING_ONLY_COLS
    : [];
  const displayHeaders = headers.filter((h) => !hiddenCols.has(h) && !scopeHidden.includes(h));
  const toggleColumn = (col) => {
    setHiddenCols((prev) => {
      const next = new Set(prev);
      if (next.has(col)) next.delete(col);
      else next.add(col);
      return next;
    });
  };

  // Fixed-position style for a popup anchored to its trigger's rect, so it
  // is never clipped by the grid's/chart's overflow. Flips to right-align
  // when it would overflow the viewport's right edge, and caps its height
  // to the space below the trigger (internal scroll for long lists).
  const menuStyle = (rect, width = 240) => {
    if (!rect) return {};
    const margin = 8;
    const left = (rect.left + width > window.innerWidth - margin)
      ? Math.max(margin, rect.right - width)
      : rect.left;
    return {
      position: 'fixed',
      top: rect.bottom + 4,
      left,
      right: 'auto',
      width,
      maxHeight: Math.max(160, window.innerHeight - rect.bottom - margin),
      overflowY: 'auto',
    };
  };

  // Place a column into exactly one chart zone (x / y / z / filter),
  // removing it from any other zone first — gives "move" semantics so a
  // chip dragged from X to Y leaves X. Header drags use this too (the
  // column isn't in any zone yet, so it's just added).
  const assignToZone = (col, zone) => {
    if (!col || !headers.includes(col)) return;
    setChartX((xs) => xs.filter((c) => c !== col));
    setChartY((ys) => ys.filter((c) => c !== col));
    setChartZ((z) => (z === col ? null : z));
    setChartFilters((f) => {
      if (!(col in f)) return f;
      const { [col]: _drop, ...rest } = f;
      return rest;
    });
    if (zone === 'x') setChartX((xs) => (xs.includes(col) ? xs : [...xs, col]));
    else if (zone === 'y') setChartY((ys) => (ys.includes(col) ? ys : [...ys, col]));
    else if (zone === 'z') setChartZ(col);
    else if (zone === 'filter') {
      const allVals = Array.from(new Set(visible.flatMap((r) => {
        const val = r[col];
        return Array.isArray(val) ? val : [val];
      }).map((v) => (v == null ? '' : v))));
      setChartFilters((f) => (col in f ? f : { ...f, [col]: allVals }));
    }
  };

  // Drag-start for a chip already sitting in a chart zone, so it can be
  // dragged to a different zone (same payload the column headers use).
  const onChipDragStart = (e, col) => {
    e.dataTransfer.setData('application/x-column', col);
    e.dataTransfer.setData('text/plain', col);
    e.dataTransfer.effectAllowed = 'move';
    setColDragging(true);
  };

  // ── Saved chart presets ──────────────────────────────────────────
  const saveCurrentPreset = () => {
    if (chartX.length === 0 && chartY.length === 0) {
      setError('Build a chart first (drop columns on the X/Y axes), then save it as a preset.');
      return;
    }
    if (chartPresets.length >= MAX_PRESETS) {
      setError(`You already have ${MAX_PRESETS} presets — delete one before saving another.`);
      return;
    }
    const name = (window.prompt('Name this chart preset:') || '').trim();
    if (!name) return;
    setChartPresets((prev) => [...prev, {
      id: 'p' + Date.now(),
      name: name.slice(0, 40),
      config: { chartType, chartX, chartY, chartZ, chartFilters, yAggregations },
    }]);
    setPresetsMenuOpen(false);
  };

  const loadPreset = (p) => {
    const c = p.config || {};
    setChartType(c.chartType || 'bar');
    setChartX(Array.isArray(c.chartX) ? c.chartX : []);
    setChartY(Array.isArray(c.chartY) ? c.chartY : []);
    setChartZ(c.chartZ ?? null);
    setChartFilters(c.chartFilters && typeof c.chartFilters === 'object' ? c.chartFilters : {});
    setYAggregations(c.yAggregations && typeof c.yAggregations === 'object' ? c.yAggregations : {});
    setPresetsMenuOpen(false);
  };

  const deletePreset = (id) => setChartPresets((prev) => prev.filter((p) => p.id !== id));

  // ── Edit mode ────────────────────────────────────────────────────
  const pendingCount = Object.values(pendingEdits).reduce((n, m) => n + Object.keys(m).length, 0);

  const toggleEditMode = async () => {
    if (!editMode && !attrOptions) {
      try {
        const r = await fetch('/api/optimizer/attribute-options');
        if (r.ok) { const d = await r.json(); if (d && d.ok) setAttrOptions(d.options || {}); }
      } catch (_) { setAttrOptions({}); }
    }
    setEditMode((v) => !v);
  };

  // Attributes are listing-level: record the edit + optimistically update
  // every row of that listing in the grid.
  const applyEdit = (listingKey, col, rawValue) => {
    const meta = EDITABLE_ATTR_COLS[col];
    if (!meta || !listingKey) return;
    setPendingEdits((prev) => ({
      ...prev,
      [listingKey]: { ...(prev[listingKey] || {}), [meta.field]: rawValue },
    }));
    const display = cap1(rawValue);
    setData((prev) => prev.map((r) => (r['Listing Key'] === listingKey ? { ...r, [col]: display } : r)));
  };

  // Multi-value edit: record a list + optimistically show it (comma-joined)
  // across all the listing's rows.
  const applyMultiEdit = (listingKey, col, field, list) => {
    if (!listingKey) return;
    setPendingEdits((prev) => ({
      ...prev,
      [listingKey]: { ...(prev[listingKey] || {}), [field]: list },
    }));
    const display = list.join(', ');
    setData((prev) => prev.map((r) => (r['Listing Key'] === listingKey ? { ...r, [col]: display } : r)));
  };

  const saveEdits = async () => {
    if (pendingCount === 0) return;
    const edits = [];
    Object.entries(pendingEdits).forEach(([lk, fields]) => {
      Object.entries(fields).forEach(([field, value]) => edits.push({ listing_key: lk, field, value }));
    });
    try {
      const r = await fetch('/api/spreadsheet/bulk-edit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...csrfHeaders() },
        body: JSON.stringify({ edits }),
      });
      if (r.status === 401) { setError('Sign in to save edits.'); return; }
      const d = await r.json();
      if (!r.ok || !d.ok) throw new Error(d.error || 'Save failed.');
      // Per-listing outcome: only a listing the server saved is badged and
      // leaves the pending set. A failed one keeps its pending edit (so
      // Save retries it) and is reported, never shown as saved.
      const results = Array.isArray(d.results) ? d.results : [];
      const savedKeys = new Set(results.filter((x) => x && x.ok).map((x) => x.listing_key));
      const failed = results.filter((x) => x && !x.ok);
      setData((prev) => prev.map((row) => (savedKeys.has(row['Listing Key']) ? { ...row, _unpushed: true } : row)));
      setPendingEdits((prev) => {
        const next = {};
        Object.entries(prev).forEach(([lk, fields]) => { if (!savedKeys.has(lk)) next[lk] = fields; });
        return next;
      });
      setAiExplanation(`Saved ${d.saved} listing${d.saved === 1 ? '' : 's'} to ListEZ — not yet pushed to Etsy.`);
      if (failed.length) {
        setError(`${failed.length} listing${failed.length === 1 ? '' : 's'} not saved `
          + `(${failed[0].error || 'failed'}) — the edits are still pending; Save retries them.`);
      }
    } catch (err) {
      setError(err.message || 'Save failed.');
    }
  };

  // Heat map: compute min/max per numeric column from VISIBLE data
  const heatStats = (() => {
    if (!heatMapOn) return {};
    const stats = {};
    headers.forEach((h) => {
      if (!isNumericCol(h)) return;
      let min = Infinity, max = -Infinity;
      visible.forEach((r) => {
        const v = r[h];
        if (typeof v === 'number') {
          if (v < min) min = v;
          if (v > max) max = v;
        }
      });
      if (min !== Infinity) stats[h] = { min, max };
    });
    return stats;
  })();

  const HEAT_KIND = {
    'Risk Score': 'bad',     // high = bad (red)
    'Progress %': 'good',    // high = good (green)
    'Hours': 'neutral',
    'Cost': 'neutral',
    'Budget': 'neutral',
    'Sprint': 'neutral',
    'Story Points': 'neutral',
  };

  const heatColor = (val, col) => {
    if (!heatMapOn) return null;
    const s = heatStats[col];
    if (!s || typeof val !== 'number') return null;
    const kind = HEAT_KIND[col] || 'neutral';
    const range = s.max - s.min;
    if (range === 0) return null;
    const tNorm = (val - s.min) / range;
    const alpha = darkMode ? 0.28 : 0.22;
    if (kind === 'good') {
      // 0 = soft red, 1 = soft green
      const r = Math.round(220 - tNorm * 160);
      const g = Math.round(120 + tNorm * 80);
      const b = Math.round(110);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
    if (kind === 'bad') {
      const r = Math.round(120 + tNorm * 130);
      const g = Math.round(180 - tNorm * 110);
      const b = Math.round(120 - tNorm * 30);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
    // neutral: transparent → terracotta accent (--ls-accent #8A3A1A).
    // All ListEZ numeric columns are 'neutral' (HEAT_KIND lists none of
    // them), so this single-hue intensity ramp is what the whole grid
    // uses — higher value = stronger terracotta. Was hardcoded Excel
    // green (16,124,65), which clashed with the site theme.
    return `rgba(138, 58, 26, ${tNorm * alpha})`;
  };

  const styles = {
    app: {
      minHeight: '100vh',
      background: t.bg,
      fontFamily: "'Manrope', system-ui, sans-serif",
      color: t.text,
      padding: '32px 20px',
    },
    container: { width: '100%', maxWidth: '100%', margin: '0 auto' },
    topBar: {
      background: ACCENT,
      color: '#fff',
      padding: '12px 20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      borderRadius: '4px 4px 0 0',
    },
    appTitle: {
      fontFamily: "'Manrope', sans-serif",
      fontWeight: 700,
      fontSize: 15,
      letterSpacing: '0.02em',
      display: 'flex',
      alignItems: 'center',
      gap: 10,
    },
    themeToggle: {
      width: 30,
      height: 30,
      borderRadius: '50%',
      background: 'rgba(255,255,255,0.18)',
      border: '1px solid rgba(255,255,255,0.3)',
      color: '#fff',
      fontSize: 14,
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.2s ease',
      padding: 0,
    },
    suggestionRow: {
      width: '100%',
      display: 'flex',
      gap: 6,
      flexWrap: 'wrap',
      marginTop: 5,
    },
    suggestionChip: {
      padding: '5px 11px',
      background: t.cardAlt,
      border: `1px solid ${t.borderLight}`,
      color: t.text2,
      fontFamily: "'Manrope', sans-serif",
      fontSize: 11,
      fontWeight: 500,
      cursor: 'pointer',
      borderRadius: 12,
      transition: 'all 0.15s ease',
    },
    insightsBtn: {
      padding: '6px 14px',
      background: 'linear-gradient(135deg, ' + ACCENT + ' 0%, ' + (darkMode ? '#3a7ca5' : '#A8602A') + ' 100%)',
      color: '#fff',
      border: 'none',
      fontFamily: "'Manrope', sans-serif",
      fontWeight: 600,
      fontSize: 13,
      letterSpacing: '0.04em',
      cursor: insightsLoading ? 'wait' : 'pointer',
      opacity: insightsLoading ? 0.6 : 1,
      borderRadius: 3,
      boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
    },
    insightsCard: {
      background: t.cardAlt,
      borderLeft: `3px solid ${ACCENT}`,
      padding: '16px 18px',
      marginTop: 12,
      marginLeft: -16,
      marginRight: -16,
      width: 'calc(100% + 32px)',
    },
    insightsHeader: {
      fontFamily: "'JetBrains Mono', monospace",
      fontSize: 10,
      letterSpacing: '0.24em',
      textTransform: 'uppercase',
      color: ACCENT,
      fontWeight: 700,
      marginBottom: 10,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    insightItem: {
      fontFamily: "'Manrope', sans-serif",
      fontSize: 13,
      color: t.text,
      lineHeight: 1.5,
      paddingLeft: 18,
      position: 'relative',
      marginBottom: 6,
    },
    insightBullet: {
      position: 'absolute',
      left: 0,
      top: 4,
      color: ACCENT,
      fontSize: 12,
    },
    aggBadge: {
      background: 'rgba(255,255,255,0.25)',
      color: '#fff',
      border: 'none',
      padding: '2px 6px',
      borderRadius: 2,
      cursor: 'pointer',
      fontSize: 9,
      fontFamily: "'JetBrains Mono', monospace",
      fontWeight: 700,
      letterSpacing: '0.1em',
    },
    aggMenu: {
      position: 'absolute',
      top: '100%',
      left: 0,
      marginTop: 4,
      background: t.card,
      border: `1px solid ${t.border}`,
      borderRadius: 4,
      boxShadow: '0 6px 16px rgba(0,0,0,0.18)',
      zIndex: 100,
      minWidth: 120,
    },
    aggMenuItem: (active) => ({
      padding: '7px 12px',
      cursor: 'pointer',
      fontFamily: "'Manrope', sans-serif",
      fontSize: 12,
      color: active ? ACCENT : t.text,
      fontWeight: active ? 600 : 400,
      background: active ? ACCENT_LIGHT : 'transparent',
    }),
    chartFilterChip: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      padding: '4px 4px 4px 10px',
      background: darkMode ? '#3a3a3a' : '#5a5a55',
      color: '#fff',
      borderRadius: 3,
      fontFamily: "'Manrope', sans-serif",
      fontSize: 11,
      fontWeight: 600,
      cursor: 'pointer',
    },
    chartFilterPopup: {
      position: 'absolute',
      top: '100%',
      left: 0,
      marginTop: 4,
      background: t.card,
      border: `1px solid ${t.border}`,
      borderRadius: 4,
      boxShadow: '0 6px 16px rgba(0,0,0,0.18)',
      zIndex: 100,
      minWidth: 180,
      maxHeight: 280,
      overflowY: 'auto',
    },
    logoSquare: {
      width: 22,
      height: 22,
      background: t.card,
      color: ACCENT,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: "'JetBrains Mono', monospace",
      fontWeight: 700,
      fontSize: 13,
      borderRadius: 2,
    },
    aiBadge: {
      fontFamily: "'JetBrains Mono', monospace",
      fontSize: 10,
      letterSpacing: '0.2em',
      textTransform: 'uppercase',
      opacity: 0.85,
    },
    aiBar: {
      background: t.card,
      borderLeft: `1px solid ${t.border}`,
      borderRight: `1px solid ${t.border}`,
      padding: '8px 14px',
      display: 'flex',
      gap: 8,
      alignItems: 'center',
      flexWrap: 'wrap',
    },
    aiInput: {
      flex: 1,
      minWidth: 240,
      padding: '6px 12px',
      border: `1px solid ${t.border}`,
      background: t.cardAlt,
      fontSize: 13,
      fontFamily: "'Manrope', sans-serif",
      outline: 'none',
      borderRadius: 3,
    },
    aiBtn: {
      padding: '6px 14px',
      background: ACCENT,
      color: '#fff',
      border: 'none',
      fontFamily: "'Manrope', sans-serif",
      fontWeight: 600,
      fontSize: 13,
      letterSpacing: '0.04em',
      cursor: loading ? 'wait' : 'pointer',
      opacity: loading ? 0.6 : 1,
      borderRadius: 3,
    },
    resetBtn: {
      padding: '6px 12px',
      background: 'transparent',
      color: t.text2,
      border: `1px solid ${t.border}`,
      fontFamily: "'Manrope', sans-serif",
      fontWeight: 500,
      fontSize: 12,
      cursor: 'pointer',
      borderRadius: 3,
    },
    aiHint: {
      width: '100%',
      fontSize: 11,
      color: t.text3,
      fontStyle: 'italic',
      marginTop: 1,
    },
    aiExplain: {
      background: ACCENT_LIGHT,
      borderLeft: `3px solid ${ACCENT}`,
      padding: '8px 14px',
      fontSize: 13,
      color: ACCENT_DARK,
      marginLeft: -16,
      marginRight: -16,
      marginTop: 12,
      width: 'calc(100% + 32px)',
    },
    errorBox: {
      background: t.errorBg,
      borderLeft: '3px solid #d4452a',
      padding: '8px 14px',
      fontSize: 13,
      color: t.errorFg,
      marginLeft: -16,
      marginRight: -16,
      marginTop: 12,
      width: 'calc(100% + 32px)',
    },
    sheetWrap: {
      background: t.card,
      border: `1px solid ${t.border}`,
      borderTop: 'none',
      borderRadius: '0 0 4px 4px',
      overflow: 'auto',
      // Was a fixed 540px, which wasted vertical space on larger screens.
      // Scale with the viewport so the grid uses the real estate freed by
      // the compacted bars; sticky header keeps column labels visible.
      maxHeight: '72vh',
    },
    table: { borderCollapse: 'collapse', width: '100%', tableLayout: 'fixed' },
    colLetterRow: { background: t.headerBg2 },
    colLetterCell: {
      borderRight: `1px solid ${t.border}`,
      borderBottom: `1px solid ${t.border}`,
      padding: '4px 0',
      textAlign: 'center',
      fontFamily: "'JetBrains Mono', monospace",
      fontSize: 11,
      color: t.text3,
      fontWeight: 500,
      background: t.headerBg2,
      position: 'sticky',
      top: 0,
      zIndex: 4,
    },
    rowNumCell: {
      background: t.headerBg2,
      borderRight: `1px solid ${t.border}`,
      borderBottom: `1px solid ${t.border}`,
      padding: '4px 8px',
      textAlign: 'center',
      fontFamily: "'JetBrains Mono', monospace",
      fontSize: 11,
      color: t.text3,
      fontWeight: 500,
      width: 40,
      cursor: (sortCol || groupBy.length > 0) ? 'default' : 'grab',
      userSelect: 'none',
      position: 'sticky',
      left: 0,
      zIndex: 3,
    },
    headerCell: (col) => ({
      background: t.headerBg,
      borderRight: `1px solid ${t.border}`,
      borderBottom: `2px solid ${t.border}`,
      padding: '10px 12px',
      textAlign: 'left',
      fontFamily: "'Manrope', sans-serif",
      fontWeight: 700,
      fontSize: 13,
      color: sortCol === col ? ACCENT : t.text,
      userSelect: 'none',
      position: 'sticky',
      top: 23,
      zIndex: 4,
    }),
    headerInner: {
      display: 'flex',
      alignItems: 'center',
      gap: 4,
    },
    headerText: {
      cursor: 'pointer',
      flex: 1,
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      // Flex items default to min-width:auto, so a single long word
      // ("Impressions") refuses to shrink below its min-content width and
      // shoves the filter button outside the cell — it became unclickable.
      // minWidth:0 lets the label shrink; overflowWrap lets an unbreakable
      // word wrap as a last resort instead of overflowing.
      minWidth: 0,
      overflowWrap: 'anywhere',
    },
    filterIcon: (active) => ({
      background: active ? ACCENT : 'transparent',
      color: active ? '#fff' : t.text3,
      border: active ? 'none' : '1px solid transparent',
      width: 22,
      height: 22,
      // Never let a long column label squeeze the button to zero width.
      flexShrink: 0,
      borderRadius: 3,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      fontSize: 11,
      padding: 0,
      transition: 'all 0.15s ease',
    }),
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 50,
    },
    dropdown: {
      position: 'absolute',
      top: '100%',
      right: 0,
      marginTop: 4,
      minWidth: 220,
      background: t.card,
      border: `1px solid ${t.border}`,
      borderRadius: 4,
      boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
      zIndex: 60,
      fontFamily: "'Manrope', sans-serif",
      fontWeight: 400,
      color: t.text,
    },
    dropdownHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: '8px 12px',
      borderBottom: `1px solid ${t.borderLight}`,
      background: t.cardAlt,
      borderRadius: '4px 4px 0 0',
    },
    dropdownLink: {
      background: 'transparent',
      border: 'none',
      color: ACCENT,
      fontFamily: "'Manrope', sans-serif",
      fontSize: 11,
      fontWeight: 600,
      cursor: 'pointer',
      padding: '2px 4px',
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
    },
    checkRow: {
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      padding: '8px 12px',
      cursor: 'pointer',
      fontSize: 13,
      borderBottom: `1px solid ${t.borderLight}`,
    },
    checkbox: {
      width: 14,
      height: 14,
      accentColor: ACCENT,
      cursor: 'pointer',
    },
    ownerCellInner: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: 4,
      alignItems: 'center',
      minHeight: 22,
    },
    ownerChip: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 26,
      height: 26,
      borderRadius: '50%',
      color: '#fff',
      fontSize: 10,
      fontFamily: "'Manrope', sans-serif",
      fontWeight: 700,
      letterSpacing: '0.02em',
      border: `2px solid ${t.card}`,
      boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
      flexShrink: 0,
    },
    ownerPlaceholder: {
      color: t.text4,
      fontStyle: 'italic',
      fontSize: 12,
      fontFamily: "'Manrope', sans-serif",
    },
    ownerCellCaret: {
      color: t.text3,
      fontSize: 9,
      marginLeft: 'auto',
      paddingLeft: 4,
    },
    addRow: {
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      padding: '8px 12px',
      borderTop: `1px solid ${t.borderLight}`,
      background: t.cardAlt,
      borderRadius: '0 0 4px 4px',
    },
    addInput: {
      flex: 1,
      padding: '5px 8px',
      border: `1px solid ${t.border}`,
      borderRadius: 3,
      fontSize: 12,
      fontFamily: "'Manrope', sans-serif",
      outline: 'none',
      background: t.card,
    },
    addBtn: {
      background: ACCENT,
      color: '#fff',
      border: 'none',
      width: 26,
      height: 26,
      borderRadius: 3,
      cursor: 'pointer',
      fontSize: 16,
      fontWeight: 700,
      lineHeight: 1,
      padding: 0,
    },
    fillHandle: {
      position: 'absolute',
      bottom: -4,
      right: -4,
      width: 9,
      height: 9,
      background: ACCENT,
      border: '1.5px solid #fff',
      cursor: 'crosshair',
      zIndex: 5,
      boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
    },
    groupByBar: {
      background: groupBarDragOver ? ACCENT_LIGHT : t.card,
      borderLeft: `1px solid ${t.border}`,
      borderRight: `1px solid ${t.border}`,
      borderTop: `1px solid ${t.borderLight}`,
      padding: '12px 16px',
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      flexWrap: 'wrap',
      minHeight: 28,
      transition: 'background 0.15s ease',
    },
    groupByLabel: {
      fontFamily: "'JetBrains Mono', monospace",
      fontSize: 10,
      letterSpacing: '0.22em',
      textTransform: 'uppercase',
      color: groupBarDragOver ? ACCENT_DARK : t.text3,
      fontWeight: 600,
    },
    groupByChip: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      padding: '6px 4px 6px 12px',
      background: ACCENT,
      color: '#fff',
      borderRadius: 4,
      fontFamily: "'Manrope', sans-serif",
      fontSize: 12,
      fontWeight: 600,
      boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
    },
    groupByChipX: {
      background: 'rgba(255,255,255,0.25)',
      color: '#fff',
      border: 'none',
      width: 18,
      height: 18,
      borderRadius: 3,
      cursor: 'pointer',
      fontSize: 12,
      lineHeight: 1,
      padding: 0,
      fontWeight: 700,
    },
    groupByEmpty: {
      fontStyle: 'italic',
      color: t.text4,
      fontSize: 13,
      fontFamily: "'Manrope', sans-serif",
    },
    groupByArrow: {
      color: t.text4,
      fontSize: 11,
      margin: '0 2px',
    },
    groupHeaderRow: (depth) => ({
      background: depth === 0 ? ACCENT_LIGHT : (darkMode ? '#1a2820' : '#F0E7D6'),
      borderTop: `1px solid ${ACCENT}55`,
      borderBottom: `1px solid ${ACCENT}55`,
      cursor: 'pointer',
      userSelect: 'none',
    }),
    groupExpandCell: {
      borderRight: `1px solid ${ACCENT}33`,
      padding: '8px 8px',
      textAlign: 'center',
      width: 40,
      fontFamily: "'JetBrains Mono', monospace",
      fontSize: 11,
      color: ACCENT_DARK,
      fontWeight: 700,
    },
    groupValueCell: (depth) => ({
      borderRight: `1px solid ${ACCENT}33`,
      padding: '8px 12px',
      paddingLeft: 12 + depth * 16,
      fontFamily: "'Manrope', sans-serif",
      fontWeight: 700,
      fontSize: 13,
      color: ACCENT_DARK,
      // Keep group headers a single Excel-style row height. Without this,
      // grouping by a long-text column (e.g. Listing titles) wraps the value
      // across many lines and the header row balloons. Match the dataCell
      // clamp: one line, ellipsis on overflow.
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    }),
    groupAggCell: {
      borderRight: `1px solid ${ACCENT}33`,
      padding: '8px 12px',
      fontFamily: "'JetBrains Mono', monospace",
      fontSize: 12,
      color: ACCENT_DARK,
      fontWeight: 600,
      textAlign: 'right',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    groupEmptyCell: {
      borderRight: `1px solid ${ACCENT}33`,
      padding: '8px 12px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
    },
    chartWrap: {
      marginBottom: 10,
      background: t.card,
      border: `1px solid ${t.border}`,
      borderRadius: 4,
      overflow: 'hidden',
    },
    chartHeader: {
      background: t.headerBg,
      borderBottom: `1px solid ${t.border}`,
      padding: '6px 14px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 12,
      flexWrap: 'wrap',
    },
    chartTitle: {
      fontFamily: "'Manrope', sans-serif",
      fontWeight: 700,
      fontSize: 13,
      color: t.text,
      display: 'flex',
      alignItems: 'center',
      gap: 8,
    },
    chartTypeRow: { display: 'flex', gap: 4 },
    chartTypeBtn: (active) => ({
      padding: '6px 12px',
      background: active ? ACCENT : '#fff',
      color: active ? '#fff' : t.text2,
      border: `1px solid ${active ? ACCENT : t.border}`,
      fontFamily: "'Manrope', sans-serif",
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      cursor: 'pointer',
      borderRadius: 3,
      display: 'inline-flex',
      alignItems: 'center',
      gap: 5,
    }),
    chartClearBtn: {
      padding: '6px 12px',
      background: 'transparent',
      color: t.text3,
      border: `1px solid ${t.border}`,
      fontFamily: "'JetBrains Mono', monospace",
      fontSize: 10,
      letterSpacing: '0.16em',
      textTransform: 'uppercase',
      cursor: 'pointer',
      borderRadius: 3,
    },
    chartAxes: {
      display: 'flex',
      gap: 10,
      padding: '8px 14px',
      borderBottom: `1px solid ${t.borderLight}`,
      background: t.cardAlt,
      flexWrap: 'wrap',
    },
    chartDrop: (active, hasValue) => ({
      flex: 1,
      minHeight: 32,
      border: `2px dashed ${active ? ACCENT : t.border}`,
      background: active ? ACCENT_LIGHT : hasValue ? t.cardAlt : t.card,
      borderRadius: 4,
      padding: '5px 10px',
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      transition: 'all 0.15s ease',
    }),
    chartAxisLabel: {
      fontFamily: "'JetBrains Mono', monospace",
      fontSize: 10,
      letterSpacing: '0.22em',
      textTransform: 'uppercase',
      color: t.text3,
      fontWeight: 600,
    },
    chartAxisChip: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      padding: '4px 4px 4px 10px',
      background: ACCENT,
      color: '#fff',
      borderRadius: 3,
      fontFamily: "'Manrope', sans-serif",
      fontSize: 12,
      fontWeight: 600,
    },
    chartAxisChipX: {
      background: 'rgba(255,255,255,0.25)',
      color: '#fff',
      border: 'none',
      width: 18,
      height: 18,
      borderRadius: 2,
      cursor: 'pointer',
      fontSize: 11,
      lineHeight: 1,
      padding: 0,
      fontWeight: 700,
    },
    chartDropEmpty: {
      fontStyle: 'italic',
      color: t.text4,
      fontSize: 12,
      fontFamily: "'Manrope', sans-serif",
    },
    chartCanvas: {
      padding: '20px 12px 12px',
      minHeight: 280,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      // Nothing the chart draws may paint over the drop zones above it.
      overflow: 'hidden',
    },
    chartLegend: {
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'center',
      gap: '4px 14px',
      maxHeight: CHART_LEGEND_MAX_H,
      overflowY: 'auto',
      padding: '6px 4px 0',
      fontSize: 12,
      fontFamily: "'Manrope', sans-serif",
      color: t.text2,
    },
    chartLegendItem: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      maxWidth: 240,
      minWidth: 0,
    },
    chartLegendSwatch: {
      width: 10,
      height: 10,
      borderRadius: 2,
      flexShrink: 0,
    },
    chartLegendText: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    chartEmpty: {
      textAlign: 'center',
      color: t.text3,
      fontStyle: 'italic',
      fontFamily: "'Manrope', sans-serif",
      fontSize: 14,
      padding: '40px 20px',
    },
    dataCell: {
      borderRight: `1px solid ${t.borderLight}`,
      borderBottom: `1px solid ${t.borderLight}`,
      padding: '8px 12px',
      fontFamily: "'JetBrains Mono', monospace",
      fontSize: 13,
      color: t.text,
      verticalAlign: 'middle',
      cursor: 'text',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    statusPill: (status) => {
      const map = {
        'Complete': { bg: t.pillCompleteBg, fg: t.pillCompleteFg },
        'In Progress': { bg: t.pillProgressBg, fg: t.pillProgressFg },
        'Not Started': { bg: t.pillNotStartedBg, fg: t.pillNotStartedFg },
        'Blocked': { bg: t.pillBlockedBg, fg: t.pillBlockedFg },
      };
      const c = map[status] || map['Not Started'];
      return {
        background: c.bg,
        color: c.fg,
        padding: '2px 10px',
        borderRadius: 12,
        fontSize: 11,
        fontFamily: "'Manrope', sans-serif",
        fontWeight: 600,
        display: 'inline-block',
      };
    },
    priorityDot: (p) => {
      const c = p === 'High' ? (darkMode ? '#ee5e3e' : '#d4452a') : p === 'Medium' ? (darkMode ? '#f0b840' : '#e8a020') : (darkMode ? '#9bc060' : '#7a9a4a');
      return {
        display: 'inline-block',
        width: 8,
        height: 8,
        borderRadius: '50%',
        background: c,
        marginRight: 8,
        verticalAlign: 'middle',
      };
    },
    editInput: {
      width: '100%',
      border: `2px solid ${ACCENT}`,
      padding: '6px 10px',
      fontFamily: "'JetBrains Mono', monospace",
      fontSize: 13,
      outline: 'none',
      background: t.card,
      boxSizing: 'border-box',
    },
    statusBar: {
      marginTop: 12,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontFamily: "'JetBrains Mono', monospace",
      fontSize: 11,
      color: t.text3,
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
    },
    dragOverIndicator: {
      borderTop: `2px solid ${ACCENT}`,
    },
  };

  return (
    <div style={styles.app}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600&display=swap');
        button:hover:not(:disabled) { filter: brightness(0.95); }
        input:focus { border-color: ${ACCENT}; }
        tr.data-row:hover td.data-cell { background: ${t.hover}; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .spin { animation: spin 0.8s linear infinite; display: inline-block; }
        @keyframes pulse-glow {
          0%, 100% { box-shadow: 0 0 0 0 ${ACCENT}66, inset 0 0 0 0 ${ACCENT}00; }
          50% { box-shadow: 0 0 0 6px ${ACCENT}00, inset 0 0 0 2px ${ACCENT}88; }
        }
        .drag-pulse { animation: pulse-glow 1.2s ease-in-out infinite; }
        .is-editing tbody td { opacity: 0.4; }
        .is-editing tbody td.editable-cell { opacity: 1; }
        ${fillDrag ? 'body, table, td { user-select: none !important; cursor: crosshair !important; }' : ''}
      `}</style>

      <div style={styles.container}>
        <div style={styles.topBar}>
          <div style={styles.appTitle}>
            <div style={styles.logoSquare}>EZ</div>

            EZ Analyzer
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{...styles.aiBadge, opacity: isAnonymous ? 0.55 : 1}}>
              {isAnonymous ? '○ Sign in for AI' : '● AI Enabled'}
            </div>
            <button
              style={styles.themeToggle}
              onClick={() => setDarkMode(!darkMode)}
              title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {darkMode ? '☀' : '☾'}
            </button>
          </div>
        </div>


        <div style={styles.chartWrap}>
          <div style={styles.chartHeader}>
            <div style={styles.chartTitle}>
              <span style={{ color: ACCENT, fontSize: 16 }}>📊</span>
              Chart {chartTitleText && <span style={{ color: '#888', fontWeight: 400, fontSize: 12 }}>— {chartTitleText}</span>}
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={styles.chartTypeRow}>
                <button style={styles.chartTypeBtn(chartType === 'bar')} onClick={() => setChartType('bar')}>▮▮ Bar</button>
                <button style={styles.chartTypeBtn(chartType === 'hbar')} onClick={() => setChartType('hbar')}>▭ H-Bar</button>
                <button style={styles.chartTypeBtn(chartType === 'line')} onClick={() => setChartType('line')}>⤴ Line</button>
                <button style={styles.chartTypeBtn(chartType === 'area')} onClick={() => setChartType('area')}>◣ Area</button>
                <button style={styles.chartTypeBtn(chartType === 'combo')} onClick={() => setChartType('combo')}>▮⤴ Combo</button>
                <button style={styles.chartTypeBtn(chartType === 'scatter')} onClick={() => setChartType('scatter')}>∴ Scatter</button>
                <button style={styles.chartTypeBtn(chartType === 'donut')} onClick={() => setChartType('donut')}>◓ Donut</button>
              </div>
              <div style={{ position: 'relative' }}>
                <button
                  style={styles.chartClearBtn}
                  onClick={(e) => { setMenuRect(e.currentTarget.getBoundingClientRect()); setPresetsMenuOpen((o) => !o); }}
                  title="Saved chart presets"
                >★ Presets{chartPresets.length ? ` (${chartPresets.length})` : ''}</button>
                {presetsMenuOpen && (
                  <>
                    <div style={styles.overlay} onClick={() => setPresetsMenuOpen(false)} />
                    <div style={{ ...styles.dropdown, ...menuStyle(menuRect, 240) }} onClick={(e) => e.stopPropagation()}>
                      <div style={styles.dropdownHeader}>
                        <button style={styles.dropdownLink} onClick={saveCurrentPreset}>＋ Save current chart</button>
                      </div>
                      {chartPresets.length === 0 ? (
                        <div style={{ padding: '10px 12px', fontSize: 12, color: t.text3, fontStyle: 'italic' }}>
                          No presets yet — build a chart, then “Save current chart”.
                        </div>
                      ) : (
                        chartPresets.map((p) => (
                          <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 8px 4px 12px' }}>
                            <button
                              style={{ ...styles.dropdownLink, color: t.text, flex: 1, textAlign: 'left' }}
                              onClick={() => loadPreset(p)}
                              title="Load this preset"
                            >{p.name}</button>
                            <button
                              style={{ ...styles.chartAxisChipX, background: 'transparent', color: t.text3 }}
                              onClick={(e) => { e.stopPropagation(); deletePreset(p.id); }}
                              title="Delete preset"
                            >✕</button>
                          </div>
                        ))
                      )}
                    </div>
                  </>
                )}
              </div>
              <button style={styles.chartClearBtn} onClick={() => { setChartX([]); setChartY([]); setChartZ(null); setChartFilters({}); setYAggregations({}); }}>Clear</button>
            </div>
          </div>

          <div style={styles.chartAxes}>
            <div
              style={styles.chartDrop(chartDragX, chartX.length > 0)}
              className={colDragging && !chartDragX ? 'drag-pulse' : ''}
              onDragOver={(e) => {
                const types = Array.from(e.dataTransfer.types || []);
                if (types.includes('application/x-column') || types.includes('text/plain')) {
                  e.preventDefault();
                  setChartDragX(true);
                }
              }}
              onDragLeave={() => setChartDragX(false)}
              onDrop={(e) => {
                e.preventDefault();
                setChartDragX(false);
                const col = e.dataTransfer.getData('application/x-column') || e.dataTransfer.getData('text/plain');
                assignToZone(col, 'x');
              }}
            >
              <span style={styles.chartAxisLabel}>X axis</span>
              {chartX.length === 0 ? (
                <span style={styles.chartDropEmpty}>Drop column(s)</span>
              ) : (
                chartX.map((f, i) => (
                  <span key={f} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    {i > 0 && <span style={{ color: '#aaa', fontSize: 11 }}>×</span>}
                    <span
                      style={{ ...styles.chartAxisChip, cursor: 'grab' }}
                      draggable
                      onDragStart={(e) => onChipDragStart(e, f)}
                      onDragEnd={() => setColDragging(false)}
                      title="Drag to another field (X / Y / Z / Filter)"
                    >
                      {f}
                      <button
                        style={styles.chartAxisChipX}
                        onClick={() => setChartX(chartX.filter((c) => c !== f))}
                        title="Remove"
                      >✕</button>
                    </span>
                  </span>
                ))
              )}
            </div>
            <div
              style={styles.chartDrop(chartDragY, chartY.length > 0)}
              className={colDragging && !chartDragY ? 'drag-pulse' : ''}
              onDragOver={(e) => {
                const types = Array.from(e.dataTransfer.types || []);
                if (types.includes('application/x-column') || types.includes('text/plain')) {
                  e.preventDefault();
                  setChartDragY(true);
                }
              }}
              onDragLeave={() => setChartDragY(false)}
              onDrop={(e) => {
                e.preventDefault();
                setChartDragY(false);
                const col = e.dataTransfer.getData('application/x-column') || e.dataTransfer.getData('text/plain');
                assignToZone(col, 'y');
              }}
            >
              <span style={styles.chartAxisLabel}>Y axis</span>
              {chartY.length === 0 ? (
                <span style={styles.chartDropEmpty}>Drop column(s)</span>
              ) : (
                chartY.map((f, i) => {
                  const currentAgg = yAggregations[f] || (isNumericColName(f) ? 'sum' : 'count');
                  const aggSymbol = { sum: 'Σ', avg: 'μ', max: '↑', min: '↓', count: '#' }[currentAgg];
                  return (
                    <span key={f} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, position: 'relative' }}>
                      {i > 0 && <span style={{ color: t.text4, fontSize: 11 }}>+</span>}
                      <span
                        style={{ ...styles.chartAxisChip, background: SERIES_COLORS[i % SERIES_COLORS.length], cursor: 'grab' }}
                        draggable
                        onDragStart={(e) => onChipDragStart(e, f)}
                        onDragEnd={() => setColDragging(false)}
                        title="Drag to another field (X / Y / Z / Filter)"
                      >
                        <button
                          style={styles.aggBadge}
                          onClick={(e) => { e.stopPropagation(); setMenuRect(e.currentTarget.getBoundingClientRect()); setOpenYAggMenu(openYAggMenu === f ? null : f); }}
                          title="Change aggregation"
                        >
                          {aggSymbol}
                        </button>
                        {f}
                        <button
                          style={styles.chartAxisChipX}
                          onClick={() => setChartY(chartY.filter((c) => c !== f))}
                          title="Remove"
                        >✕</button>
                      </span>
                      {openYAggMenu === f && (
                        <>
                          <div style={styles.overlay} onClick={(e) => { e.stopPropagation(); setOpenYAggMenu(null); }} />
                          <div style={{ ...styles.aggMenu, ...menuStyle(menuRect, 150) }} onClick={(e) => e.stopPropagation()}>
                            {['sum', 'avg', 'max', 'min', 'count'].map((opt) => (
                              <div
                                key={opt}
                                style={styles.aggMenuItem(currentAgg === opt)}
                                onClick={() => { setYAggregations({ ...yAggregations, [f]: opt }); setOpenYAggMenu(null); }}
                              >
                                {opt.charAt(0).toUpperCase() + opt.slice(1)}
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </span>
                  );
                })
              )}
            </div>
            <div
              style={styles.chartDrop(chartDragZ, !!chartZ)}
              className={colDragging && !chartDragZ ? 'drag-pulse' : ''}
              onDragOver={(e) => {
                const types = Array.from(e.dataTransfer.types || []);
                if (types.includes('application/x-column') || types.includes('text/plain')) {
                  e.preventDefault();
                  setChartDragZ(true);
                }
              }}
              onDragLeave={() => setChartDragZ(false)}
              onDrop={(e) => {
                e.preventDefault();
                setChartDragZ(false);
                const col = e.dataTransfer.getData('application/x-column') || e.dataTransfer.getData('text/plain');
                assignToZone(col, 'z');
              }}
            >
              <span style={styles.chartAxisLabel}>Z {chartType === 'scatter' ? '(size)' : '(series)'}</span>
              {chartZ ? (
                <span
                  style={{ ...styles.chartAxisChip, background: '#3a7ca5', cursor: 'grab' }}
                  draggable
                  onDragStart={(e) => onChipDragStart(e, chartZ)}
                  onDragEnd={() => setColDragging(false)}
                  title="Drag to another field (X / Y / Z / Filter)"
                >
                  {chartZ}
                  <button
                    style={styles.chartAxisChipX}
                    onClick={() => setChartZ(null)}
                    title="Remove"
                  >✕</button>
                </span>
              ) : (
                <span style={styles.chartDropEmpty}>Optional</span>
              )}
            </div>
            <div
              style={styles.chartDrop(chartFilterDragOver, Object.keys(chartFilters).length > 0)}
              className={colDragging && !chartFilterDragOver ? 'drag-pulse' : ''}
              onDragOver={(e) => {
                const types = Array.from(e.dataTransfer.types || []);
                if (types.includes('application/x-column') || types.includes('text/plain')) {
                  e.preventDefault();
                  setChartFilterDragOver(true);
                }
              }}
              onDragLeave={() => setChartFilterDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setChartFilterDragOver(false);
                const col = e.dataTransfer.getData('application/x-column') || e.dataTransfer.getData('text/plain');
                assignToZone(col, 'filter');
              }}
            >
              <span style={styles.chartAxisLabel}>Filter (chart only)</span>
              {Object.keys(chartFilters).length === 0 ? (
                <span style={styles.chartDropEmpty}>Optional</span>
              ) : (
                Object.entries(chartFilters).map(([col, allowed]) => {
                  const allVals = Array.from(new Set(visible.flatMap((r) => {
                    const v = r[col];
                    return Array.isArray(v) ? v : [v];
                  })));
                  const isFull = allowed.length === allVals.length;
                  return (
                    <span key={col} style={{ display: 'inline-flex', alignItems: 'center', position: 'relative' }}>
                      <span
                        style={{ ...styles.chartFilterChip, cursor: 'grab' }}
                        draggable
                        onDragStart={(e) => onChipDragStart(e, col)}
                        onDragEnd={() => setColDragging(false)}
                        onClick={(e) => { e.stopPropagation(); setMenuRect(e.currentTarget.getBoundingClientRect()); setOpenChartFilter(openChartFilter === col ? null : col); }}
                        title="Click to edit · drag to another field"
                      >
                        {col}
                        <span style={{ fontSize: 10, opacity: 0.8, marginLeft: 2 }}>
                          ({isFull ? 'all' : allowed.length + '/' + allVals.length})
                        </span>
                        <button
                          style={styles.chartAxisChipX}
                          onClick={(e) => {
                            e.stopPropagation();
                            const { [col]: _, ...rest } = chartFilters;
                            setChartFilters(rest);
                          }}
                          title="Remove filter"
                        >✕</button>
                      </span>
                      {openChartFilter === col && (
                        <>
                          <div style={styles.overlay} onClick={(e) => { e.stopPropagation(); setOpenChartFilter(null); }} />
                          <div style={{ ...styles.chartFilterPopup, ...menuStyle(menuRect) }} onClick={(e) => e.stopPropagation()}>
                            <div style={styles.dropdownHeader}>
                              <button
                                style={styles.dropdownLink}
                                onClick={() => setChartFilters({ ...chartFilters, [col]: allVals })}
                              >
                                All
                              </button>
                              <button
                                style={styles.dropdownLink}
                                onClick={() => setChartFilters({ ...chartFilters, [col]: [] })}
                              >
                                None
                              </button>
                            </div>
                            {allVals.map((val) => (
                              <label key={String(val)} style={styles.checkRow}>
                                <input
                                  type="checkbox"
                                  style={styles.checkbox}
                                  checked={allowed.includes(val)}
                                  onChange={() => {
                                    const next = allowed.includes(val)
                                      ? allowed.filter((v) => v !== val)
                                      : [...allowed, val];
                                    setChartFilters({ ...chartFilters, [col]: next });
                                  }}
                                />
                                {col === 'Status' ? (
                                  <span style={styles.statusPill(val)}>{val}</span>
                                ) : col === 'Priority' ? (
                                  <span><span style={styles.priorityDot(val)}></span>{val}</span>
                                ) : col === 'Owner' ? (
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                    <span
                                      style={{
                                        ...styles.ownerChip,
                                        background: ownerColor(val),
                                        width: 20,
                                        height: 20,
                                        fontSize: 9,
                                        border: 'none',
                                        boxShadow: 'none',
                                      }}
                                    >
                                      {ownerInitials(val)}
                                    </span>
                                    {val}
                                  </span>
                                ) : (
                                  <span style={(val === '' || val == null) ? { fontStyle: 'italic', color: t.text3 } : undefined}>
                                    {(val === '' || val == null) ? '(blank)' : String(val)}
                                  </span>
                                )}
                              </label>
                            ))}
                          </div>
                        </>
                      )}
                    </span>
                  );
                })
              )}
            </div>
          </div>

          <div style={styles.chartCanvas}>
            {(chartX.length === 0 || chartY.length === 0) ? (
              <div style={styles.chartEmpty}>
                Drag column headers up to the <b>X</b> and <b>Y</b> drop zones to build your chart.
                <br /><br />
                <span style={{ fontSize: 12, color: '#aaa' }}>
                  Multiple Y → multi-series. Multiple X → combined categories. <b>Z</b> breaks series down by a third dimension (or sizes scatter points).
                </span>
              </div>
            ) : chartData.length === 0 ? (
              <div style={styles.chartEmpty}>
                No chartable data. Try a different X/Y combination or switch chart type.
              </div>
            ) : (
              <>
                {chartCapNote && (
                  <div style={{ position: 'absolute', top: 4, left: 16, fontSize: 11, color: t.text3, fontStyle: 'italic' }}>
                    {chartCapNote}
                  </div>
                )}
                {chartType === 'scatter' && (chartX.length > 1 || chartY.length > 1) && (
                  <div style={{ position: 'absolute', top: 12, right: 16, fontSize: 11, color: '#999', fontStyle: 'italic' }}>
                    Scatter uses first X &amp; Y only
                  </div>
                )}
                <ResponsiveContainer width="100%" height={320}>
                  {chartType === 'bar' ? (
                    <BarChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 30 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e4" />
                      <XAxis dataKey="x" tick={{ fontSize: 11, fontFamily: "'Manrope', sans-serif" }} angle={-20} textAnchor="end" height={60} />
                      <YAxis tick={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace" }} />
                      <Tooltip
                        contentStyle={{ background: '#fff', border: `1px solid ${ACCENT}`, borderRadius: 3, fontFamily: "'Manrope', sans-serif", fontSize: 12 }}
                        formatter={(v, name) => [typeof v === 'number' ? v.toLocaleString() : v, chartZ ? `${chartZ}: ${name}` : seriesLabel(name)]}
                      />
                      {chartSeriesKeys.length > 1 && chartLegend((v) => (chartZ ? v : seriesLabel(v)))}
                      {chartSeriesKeys.map((key, i) => (
                        <Bar key={key} dataKey={key} fill={seriesColor(key, i)} radius={[3, 3, 0, 0]} />
                      ))}
                    </BarChart>
                  ) : chartType === 'hbar' ? (
                    <BarChart data={chartData} layout="vertical" margin={{ top: 10, right: 20, left: 10, bottom: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e4" />
                      <XAxis type="number" tick={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace" }} />
                      <YAxis type="category" dataKey="x" width={150} tick={{ fontSize: 11, fontFamily: "'Manrope', sans-serif" }} />
                      <Tooltip
                        contentStyle={{ background: '#fff', border: `1px solid ${ACCENT}`, borderRadius: 3, fontFamily: "'Manrope', sans-serif", fontSize: 12 }}
                        formatter={(v, name) => [typeof v === 'number' ? v.toLocaleString() : v, chartZ ? `${chartZ}: ${name}` : seriesLabel(name)]}
                      />
                      {chartSeriesKeys.length > 1 && chartLegend((v) => (chartZ ? v : seriesLabel(v)))}
                      {chartSeriesKeys.map((key, i) => (
                        <Bar key={key} dataKey={key} fill={seriesColor(key, i)} radius={[0, 3, 3, 0]} />
                      ))}
                    </BarChart>
                  ) : chartType === 'line' ? (
                    <LineChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 30 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e4" />
                      <XAxis dataKey="x" tick={{ fontSize: 11, fontFamily: "'Manrope', sans-serif" }} angle={-20} textAnchor="end" height={60} />
                      <YAxis tick={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace" }} />
                      <Tooltip
                        contentStyle={{ background: '#fff', border: `1px solid ${ACCENT}`, borderRadius: 3, fontFamily: "'Manrope', sans-serif", fontSize: 12 }}
                        formatter={(v, name) => [typeof v === 'number' ? v.toLocaleString() : v, chartZ ? `${chartZ}: ${name}` : seriesLabel(name)]}
                      />
                      {chartSeriesKeys.length > 1 && chartLegend((v) => (chartZ ? v : seriesLabel(v)))}
                      {chartSeriesKeys.map((key, i) => (
                        <Line key={key} type="monotone" dataKey={key} stroke={seriesColor(key, i)} strokeWidth={2.5} dot={{ fill: seriesColor(key, i), r: 3 }} activeDot={{ r: 6 }} />
                      ))}
                    </LineChart>
                  ) : chartType === 'area' ? (
                    <AreaChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 30 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e4" />
                      <XAxis dataKey="x" tick={{ fontSize: 11, fontFamily: "'Manrope', sans-serif" }} angle={-20} textAnchor="end" height={60} />
                      <YAxis tick={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace" }} />
                      <Tooltip
                        contentStyle={{ background: '#fff', border: `1px solid ${ACCENT}`, borderRadius: 3, fontFamily: "'Manrope', sans-serif", fontSize: 12 }}
                        formatter={(v, name) => [typeof v === 'number' ? v.toLocaleString() : v, chartZ ? `${chartZ}: ${name}` : seriesLabel(name)]}
                      />
                      {chartSeriesKeys.length > 1 && chartLegend((v) => (chartZ ? v : seriesLabel(v)))}
                      {chartSeriesKeys.map((key, i) => (
                        <Area key={key} type="monotone" dataKey={key} stroke={seriesColor(key, i)} fill={seriesColor(key, i)} fillOpacity={0.18} strokeWidth={2} />
                      ))}
                    </AreaChart>
                  ) : chartType === 'combo' ? (
                    <ComposedChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 30 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e4" />
                      <XAxis dataKey="x" tick={{ fontSize: 11, fontFamily: "'Manrope', sans-serif" }} angle={-20} textAnchor="end" height={60} />
                      <YAxis yAxisId="left" tick={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace" }} />
                      <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace" }} />
                      <Tooltip
                        contentStyle={{ background: '#fff', border: `1px solid ${ACCENT}`, borderRadius: 3, fontFamily: "'Manrope', sans-serif", fontSize: 12 }}
                        formatter={(v, name) => [typeof v === 'number' ? v.toLocaleString() : v, chartZ ? `${chartZ}: ${name}` : seriesLabel(name)]}
                      />
                      {chartLegend((v) => (chartZ ? v : seriesLabel(v)))}
                      {chartSeriesKeys.map((key, i) => (
                        i === 0 ? (
                          <Bar key={key} yAxisId="left" dataKey={key} fill={seriesColor(key, i)} radius={[3, 3, 0, 0]} />
                        ) : (
                          <Line key={key} yAxisId="right" type="monotone" dataKey={key} stroke={seriesColor(key, i)} strokeWidth={2.5} dot={{ fill: seriesColor(key, i), r: 3 }} activeDot={{ r: 6 }} />
                        )
                      ))}
                    </ComposedChart>
                  ) : chartType === 'donut' ? (
                    <PieChart margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
                      <Tooltip
                        contentStyle={{ background: '#fff', border: `1px solid ${ACCENT}`, borderRadius: 3, fontFamily: "'Manrope', sans-serif", fontSize: 12 }}
                        formatter={(v, n, p) => [typeof v === 'number' ? v.toLocaleString() : v, (p && p.payload && p.payload.x != null) ? p.payload.x : n]}
                      />
                      {chartLegend((v) => v)}
                      <Pie data={chartData} dataKey={chartSeriesKeys[0]} nameKey="x" cx="50%" cy="50%" innerRadius={58} outerRadius={108} paddingAngle={2}>
                        {chartData.map((entry, i) => (
                          <Cell key={i} fill={seriesColor(entry.x, i)} />
                        ))}
                      </Pie>
                    </PieChart>
                  ) : (
                    <ScatterChart margin={{ top: 10, right: 20, left: 10, bottom: 30 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e4" />
                      <XAxis type="number" dataKey="x" name={chartX[0]} tick={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace" }} label={{ value: chartX[0], position: 'insideBottom', offset: -10, style: { fontSize: 11, fill: '#888', fontFamily: "'Manrope', sans-serif" } }} />
                      <YAxis type="number" dataKey="y" name={chartY[0]} tick={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace" }} label={{ value: chartY[0], angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: '#888', fontFamily: "'Manrope', sans-serif" } }} />
                      {chartZ && <ZAxis type="number" dataKey="z" name={chartZ} range={[60, 800]} />}
                      <Tooltip
                        cursor={{ strokeDasharray: '3 3' }}
                        contentStyle={{ background: '#fff', border: `1px solid ${ACCENT}`, borderRadius: 3, fontFamily: "'Manrope', sans-serif", fontSize: 12 }}
                      />
                      <Scatter data={chartData} fill={ACCENT} fillOpacity={0.6} />
                    </ScatterChart>
                  )}
                </ResponsiveContainer>
              </>
            )}
          </div>
        </div>

        <div style={styles.aiBar}>
          <input
            style={styles.aiInput}
            value={aiCommand}
            onChange={(e) => setAiCommand(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && runAI()}
            placeholder='Try: "show only winning pins" or "sort by saves, highest first"'
            disabled={loading}
          />
          <button style={styles.aiBtn} onClick={runAI} disabled={loading}>
            {loading ? <span className="spin">↻</span> : '✦'} Ask Claude
          </button>
          <button
            style={styles.insightsBtn}
            onClick={analyzeData}
            disabled={insightsLoading}
            title="Have Claude analyze the visible data and surface insights"
          >
            {insightsLoading ? <span className="spin">↻</span> : '✨'} Analyze Data
          </button>
          <button style={styles.resetBtn} onClick={reset}>Reset Sheet</button>
          <button
            style={{
              ...styles.resetBtn,
              borderColor: heatMapOn ? ACCENT : t.border,
              color: heatMapOn ? ACCENT : t.text2,
              background: heatMapOn ? ACCENT_LIGHT : 'transparent',
            }}
            onClick={() => setHeatMapOn(!heatMapOn)}
            title="Toggle heat-map shading on numeric columns"
          >
            🌡 Heat-map
          </button>
          <div style={{ display: 'inline-flex', border: `1px solid ${t.border}`, borderRadius: 3, overflow: 'hidden' }} title="Data scope">
            {[['both', 'Both'], ['etsy', 'Etsy'], ['pinterest', 'Pinterest']].map(([val, label]) => (
              <button
                key={val}
                onClick={() => setScope(val)}
                style={{
                  padding: '6px 10px',
                  border: 'none',
                  background: scope === val ? ACCENT : 'transparent',
                  color: scope === val ? '#fff' : t.text2,
                  fontFamily: "'Manrope', sans-serif",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
                title={val === 'etsy'
                  ? 'One row per listing (Etsy/listing data)'
                  : val === 'pinterest'
                    ? 'One row per pin (Pinterest data + listing title)'
                    : 'Every pin + all columns'}
              >
                {label}
              </button>
            ))}
          </div>
          <button
            style={{
              ...styles.resetBtn,
              borderColor: dailyView ? ACCENT : t.border,
              color: dailyView ? ACCENT : t.text2,
              background: dailyView ? ACCENT_LIGHT : 'transparent',
            }}
            onClick={() => {
              const entering = !dailyView;
              setDailyView(entering);
              if (entering) {
                // Switching into daily mode changes the columns, so clear any
                // chart/group/filter/sort state that referenced the old columns.
                // This clear is NOT persisted (auto-save skips daily mode).
                setChartX([]); setChartY([]); setChartZ(null);
                setChartFilters({}); setYAggregations({});
                setGroupBy([]); setCollapsedGroups(new Set());
                setColFilters({}); setNumFilters({}); setSortCol(null); setFilterIds(null);
              } else {
                // Leaving daily mode — restore the saved normal-mode chart
                // config so the user's built chart comes back.
                try {
                  const saved = JSON.parse(localStorage.getItem(LS_SETTINGS_KEY) || '{}');
                  setChartX(Array.isArray(saved.chartX) ? saved.chartX : []);
                  setChartY(Array.isArray(saved.chartY) ? saved.chartY : []);
                  setChartZ(saved.chartZ !== undefined ? saved.chartZ : null);
                  setChartFilters(saved.chartFilters && typeof saved.chartFilters === 'object' ? saved.chartFilters : {});
                  setYAggregations(saved.yAggregations && typeof saved.yAggregations === 'object' ? saved.yAggregations : {});
                  setGroupBy(Array.isArray(saved.groupBy) ? saved.groupBy : []);
                  setCollapsedGroups(new Set(Array.isArray(saved.collapsedGroups) ? saved.collapsedGroups : []));
                  setColFilters(saved.colFilters && typeof saved.colFilters === 'object' ? saved.colFilters : {});
                  setNumFilters(saved.numFilters && typeof saved.numFilters === 'object' ? saved.numFilters : {});
                  setSortCol(saved.sortCol !== undefined ? saved.sortCol : null);
                  setSortDir(typeof saved.sortDir === 'string' ? saved.sortDir : 'asc');
                } catch (_) { /* corrupt or absent — leave cleared */ }
                setFilterIds(null);
              }
            }}
            title="One row per pin per day (per-day deltas) — drag Date to the chart X axis + a metric to Y for a trendline"
          >
            📈 {dailyView ? 'Daily trend: ON' : 'Daily trend'}
          </button>
          <div style={{ position: 'relative' }}>
            <button
              style={styles.resetBtn}
              onClick={(e) => { setMenuRect(e.currentTarget.getBoundingClientRect()); setColumnsMenuOpen((o) => !o); }}
              title="Show or hide columns"
            >
              ⊞ Columns{hiddenCols.size ? ` (${displayHeaders.length}/${headers.length})` : ''}
            </button>
            {columnsMenuOpen && (
              <>
                <div style={styles.overlay} onClick={() => setColumnsMenuOpen(false)} />
                <div
                  style={{ ...styles.dropdown, ...menuStyle(menuRect, 220) }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div style={styles.dropdownHeader}>
                    <button style={styles.dropdownLink} onClick={() => setHiddenCols(new Set())}>Show all</button>
                  </div>
                  {headers.map((h) => (
                    <label key={h} style={styles.checkRow}>
                      <input
                        type="checkbox"
                        style={styles.checkbox}
                        checked={!hiddenCols.has(h)}
                        onChange={() => toggleColumn(h)}
                      />
                      {h}
                    </label>
                  ))}
                </div>
              </>
            )}
          </div>
          <button
            style={{ ...styles.resetBtn, borderColor: editMode ? ACCENT : t.border, color: editMode ? ACCENT : t.text2, background: editMode ? ACCENT_LIGHT : 'transparent', opacity: isAnonymous ? 0.5 : 1 }}
            onClick={() => { if (isAnonymous) { setError('Sign in to edit your listings.'); return; } toggleEditMode(); }}
            title={isAnonymous ? 'Sign in to edit' : 'Edit attributes — saves to ListEZ; push to Etsy separately'}
          >✎ {editMode ? 'Editing' : 'Edit'}</button>
          {editMode && pendingCount > 0 && (
            <button style={styles.aiBtn} onClick={saveEdits} title="Save edits to ListEZ (not pushed to Etsy)">
              Save {pendingCount} change{pendingCount === 1 ? '' : 's'}
            </button>
          )}
          <div style={styles.suggestionRow}>
            {[
              'Show only winning pins',
              'Sort by saves, highest first',
              'Pins with CTR over 2%',
              'Lowest Overall Score first',
              'Hide dead pins',
            ].map((s) => (
              <button
                key={s}
                style={styles.suggestionChip}
                onClick={() => { setAiCommand(s); runAI(s); }}
              >
                {s}
              </button>
            ))}
          </div>
          <div style={styles.aiHint}>
            Drag column headers to the <b>Chart</b>, <b>Group By</b> bar, or use natural-language commands above. Click cells to select + fill, ▾ to filter rows, double-click to edit.
          </div>
          {aiExplanation && <div style={styles.aiExplain}>✦ {aiExplanation}</div>}
          {insights && (
            <div style={styles.insightsCard}>
              <div style={styles.insightsHeader}>
                <span>✨ Claude's analysis of {visible.length} rows</span>
                <button
                  style={{ ...styles.dropdownLink, color: t.text3 }}
                  onClick={() => setInsights(null)}
                >
                  Dismiss
                </button>
              </div>
              {insights.map((insight, i) => (
                <div key={i} style={styles.insightItem}>
                  <span style={styles.insightBullet}>◆</span>
                  {insight}
                </div>
              ))}
            </div>
          )}
          {error && <div style={styles.errorBox}>{error}</div>}
        </div>

        <div
          style={styles.groupByBar}
          className={colDragging && !groupBarDragOver ? 'drag-pulse' : ''}
          onDragOver={(e) => {
            const types = Array.from(e.dataTransfer.types || []);
            if (types.includes('application/x-column') || types.includes('text/plain')) {
              e.preventDefault();
              setGroupBarDragOver(true);
            }
          }}
          onDragLeave={() => setGroupBarDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setGroupBarDragOver(false);
            const col = e.dataTransfer.getData('application/x-column') || e.dataTransfer.getData('text/plain');
            if (col && headers.includes(col) && !groupBy.includes(col)) {
              addGroupBy(col);
            }
          }}
        >
          <span style={styles.groupByLabel}>
            {groupBarDragOver ? '▼ Drop to group by' : '⧉ Group by'}
          </span>
          {groupBy.length === 0 && !groupBarDragOver && (
            <span style={styles.groupByEmpty}>
              Drag a column header here to group rows by its values
            </span>
          )}
          {groupBy.map((col, i) => (
            <span key={col} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {i > 0 && <span style={styles.groupByArrow}>→</span>}
              <span style={styles.groupByChip}>
                {col}
                <button
                  style={styles.groupByChipX}
                  onClick={() => removeGroupBy(col)}
                  title="Remove grouping"
                >
                  ✕
                </button>
              </span>
            </span>
          ))}
        </div>

        <div style={styles.sheetWrap} onScroll={onSheetScroll}>
          <table style={styles.table} className={editMode ? 'is-editing' : ''}>
            <colgroup>
              <col style={{ width: 40 }} />
              {displayHeaders.map((h, i) => (
                <col
                  key={i}
                  style={{
                    // table-layout:fixed + table width:100% means these values
                    // act as RATIOS dividing the full grid width — so the grid
                    // always fills the screen, and text-heavy ListEZ columns
                    // (Listing, Pin Title) get proportionally more room than
                    // the narrow numeric/score columns.
                    // The fallback width also has to fit the header itself:
                    // label + filter button + padding. Multi-word labels wrap,
                    // so only the LONGEST WORD sets the floor — that's what
                    // made narrow columns with one long word ("Impressions",
                    // "Orientation") clip their filter button.
                    width:
                      h === 'Listing' || h === 'Pin Title' ? 240 :
                      h === 'Listing Key' || h === 'Board' ? 140 :
                      h === 'Posted At' ? 120 :
                      h === 'Type' || h === 'Status' || h === 'Verdict' ? 95 :
                      h === 'Pin #' ? 60 :
                      headerFallbackWidth(h),
                  }}
                />
              ))}
            </colgroup>
            <thead>
              <tr style={styles.colLetterRow}>
                <th style={styles.colLetterCell}></th>
                {displayHeaders.map((_, i) => (
                  <th key={i} style={styles.colLetterCell}>{colLetter(i)}</th>
                ))}
              </tr>
              <tr>
                <th style={{ ...styles.headerCell(null), background: '#f1f1ee' }}></th>
                {displayHeaders.map((h) => {
                  // Every column gets a filter dropdown. TEXT columns get the
                  // discrete checkbox list; NUMERIC columns (counts, scores,
                  // CTR %) get a condition editor (e.g. Impressions > 0),
                  // since listing thousands of distinct numbers as checkboxes
                  // is unusable.
                  const numericCol = isNumericCol(h);
                  const hasFilterDropdown = true;
                  const filterActive = numericCol ? isNumFiltered(h) : isColFiltered(h);
                  return (
                    <th
                      key={h}
                      style={styles.headerCell(h)}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData('text/plain', h);
                        e.dataTransfer.setData('application/x-column', h);
                        e.dataTransfer.effectAllowed = 'copy';
                        setColDragging(true);
                      }}
                      onDragEnd={() => setColDragging(false)}
                    >
                      <div style={styles.headerInner}>
                        <span style={styles.headerText} onClick={() => toggleSort(h)}>
                          {h}
                          {sortCol === h && (
                            <span style={{ fontSize: 11 }}>
                              {sortDir === 'asc' ? '▲' : '▼'}
                            </span>
                          )}
                        </span>
                        {hasFilterDropdown && (
                          <button
                            style={styles.filterIcon(filterActive)}
                            onClick={(e) => {
                              e.stopPropagation();
                              setMenuRect(e.currentTarget.getBoundingClientRect());
                              setOpenFilter(openFilter === h ? null : h);
                            }}
                            title={filterActive
                              ? (numericCol
                                  ? `Filter active: ${h} ${numFilters[h].op} ${numFilters[h].value} — click to edit`
                                  : 'Filter active — click to edit')
                              : 'Filter by ' + h}
                          >
                            ▾
                          </button>
                        )}
                      </div>
                      {openFilter === h && (
                        <>
                          <div style={styles.overlay} onClick={() => setOpenFilter(null)} />
                          <div style={{ ...styles.dropdown, ...menuStyle(menuRect) }} onClick={(e) => e.stopPropagation()}>
                            {numericCol ? (
                              <div style={{ padding: 10, minWidth: 210 }}>
                                <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                                  <select
                                    value={(numFilters[h] && numFilters[h].op) || '>'}
                                    onChange={(e) => setNumFilter(h, { op: e.target.value })}
                                    style={{ fontSize: 12, fontFamily: "'JetBrains Mono', monospace", border: `1px solid ${t.border}`, borderRadius: 3, padding: '3px 4px', background: t.card, color: t.text }}
                                  >
                                    {NUM_OPS.map((op) => (
                                      <option key={op} value={op}>{op}</option>
                                    ))}
                                  </select>
                                  <input
                                    type="number"
                                    autoFocus
                                    placeholder="value"
                                    value={(numFilters[h] && numFilters[h].value) ?? ''}
                                    onChange={(e) => setNumFilter(h, { value: e.target.value })}
                                    onKeyDown={(e) => { if (e.key === 'Enter') setOpenFilter(null); }}
                                    style={{ flex: 1, width: '100%', minWidth: 0, fontSize: 12, fontFamily: "'JetBrains Mono', monospace", border: `1px solid ${t.border}`, borderRadius: 3, padding: '3px 6px', background: t.inputBg || t.card, color: t.text }}
                                  />
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                  <button
                                    style={styles.dropdownLink}
                                    onClick={() => { setNumFilter(h, { op: '!=', value: '0' }); setOpenFilter(null); }}
                                    title={`Show only rows where ${h} is not zero`}
                                  >
                                    Non-zero
                                  </button>
                                  <button
                                    style={styles.dropdownLink}
                                    onClick={() => { setNumFilter(h, { op: '>', value: '0' }); setOpenFilter(null); }}
                                  >
                                    &gt; 0
                                  </button>
                                  <button style={styles.dropdownLink} onClick={() => { clearNumFilter(h); setOpenFilter(null); }}>
                                    Clear
                                  </button>
                                </div>
                                <div style={{ marginTop: 8, fontSize: 10, color: t.text3, lineHeight: 1.4 }}>
                                  Blank cells are excluded by any condition.
                                </div>
                              </div>
                            ) : (
                            <>
                            <div style={styles.dropdownHeader}>
                              <button style={styles.dropdownLink} onClick={() => selectAllForCol(h)}>
                                Select All
                              </button>
                              <button style={styles.dropdownLink} onClick={() => clearForCol(h)}>
                                Clear
                              </button>
                            </div>
                            {uniqueValues(h).map((value) => (
                              <label key={value} style={styles.checkRow}>
                                <input
                                  type="checkbox"
                                  style={styles.checkbox}
                                  checked={isValueChecked(h, value)}
                                  onChange={() => toggleColValue(h, value)}
                                />
                                {h === 'Status' ? (
                                  <span style={styles.statusPill(value)}>{value}</span>
                                ) : h === 'Priority' ? (
                                  <span><span style={styles.priorityDot(value)}></span>{value}</span>
                                ) : (
                                  <span style={(value === '' || value == null) ? { fontStyle: 'italic', color: t.text3 } : undefined}>
                                    {(value === '' || value == null) ? '(blank)' : value}
                                  </span>
                                )}
                              </label>
                            ))}
                            </>
                            )}
                          </div>
                        </>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody onDrop={onDrop} onDragEnd={onDragEnd}>
              {visible.length === 0 && (
                <tr>
                  <td colSpan={displayHeaders.length + 1} style={{ ...styles.dataCell, textAlign: 'center', padding: '40px', color: '#888880', fontStyle: 'italic' }}>
                    No rows match. Try "reset" or a different command.
                  </td>
                </tr>
              )}
              {(() => {
                const renderGroupHeader = (item) => (
                  <tr
                    key={item.key}
                    style={styles.groupHeaderRow(item.depth)}
                    onClick={() => toggleGroupCollapse(item.key)}
                  >
                    <td style={styles.groupExpandCell}>{item.collapsed ? '▶' : '▼'}</td>
                    {displayHeaders.map((col) => {
                      if (col === item.col) {
                        return (
                          <td key={col} style={styles.groupValueCell(item.depth)}>
                            {col}: <strong>{item.value}</strong>
                            <span style={{ opacity: 0.6, fontSize: 11, marginLeft: 6, fontFamily: "'JetBrains Mono', monospace" }}>
                              ({item.count})
                            </span>
                          </td>
                        );
                      }
                      if (item.aggregates[col] !== undefined) {
                        const val = item.aggregates[col];
                        return (
                          <td key={col} style={styles.groupAggCell}>
                            Σ {col === 'Cost' ? '$' + val.toLocaleString() : val.toLocaleString()}
                          </td>
                        );
                      }
                      return <td key={col} style={styles.groupEmptyCell}></td>;
                    })}
                  </tr>
                );
                const renderDataRow = (row, visIdx) => {
                const dataIdx = idToDataIdx.has(row._id) ? idToDataIdx.get(row._id) : -1;
                const isOver = dragOverIdx === dataIdx && draggedIdx !== dataIdx;
                const isGrouped = groupBy.length > 0;
                return (
                  <tr
                    key={row._id}
                    className="data-row"
                    draggable={!sortCol && !isGrouped}
                    onDragStart={!isGrouped ? (e) => onDragStart(e, visIdx) : undefined}
                    onDragOver={!isGrouped ? (e) => onDragOver(e, visIdx) : undefined}
                    style={{
                      opacity: draggedIdx === dataIdx ? 0.4 : 1,
                      ...(isOver ? styles.dragOverIndicator : {}),
                    }}
                  >
                    <td style={styles.rowNumCell} title={row._unpushed ? 'Saved to ListEZ — not yet pushed to Etsy' : (isGrouped ? '' : sortCol ? 'Clear sort to drag rows' : 'Drag to reorder')}>
                      {row._unpushed ? <span style={{ color: '#A8742A', fontSize: 12 }} aria-label="unpushed" title="Saved to ListEZ — not yet pushed to Etsy">▲</span> : (visIdx + 1)}
                    </td>
                    {displayHeaders.map((col) => {
                      const v = row[col];

                      // Edit mode: editable attribute columns render a
                      // controlled-vocab dropdown. The value applies to the
                      // parent listing (and all its pin rows).
                      if (editMode && EDITABLE_ATTR_COLS[col]) {
                        const meta = EDITABLE_ATTR_COLS[col];
                        const opts = (attrOptions && attrOptions[meta.opt]) || [];
                        return (
                          <td key={col} className="editable-cell" style={{ ...styles.dataCell, background: ACCENT_LIGHT, overflow: 'visible' }}>
                            <select
                              value={(v == null ? '' : v).toString().toLowerCase()}
                              onChange={(e) => applyEdit(row['Listing Key'], col, e.target.value)}
                              style={{ width: '100%', fontSize: 12, fontFamily: "'Manrope', sans-serif", border: `1px solid ${ACCENT}`, borderRadius: 3, padding: '2px 4px', background: t.card, color: t.text }}
                            >
                              <option value="">—</option>
                              {opts.map((o) => (
                                <option key={o} value={o}>{cap1(o)}</option>
                              ))}
                            </select>
                          </td>
                        );
                      }

                      // Edit mode: multi-value chip editor (Tags free-text;
                      // Room/Subject/Home Style = full controlled vocab).
                      if (editMode && EDITABLE_MULTI_COLS[col]) {
                        const meta = EDITABLE_MULTI_COLS[col];
                        const lk = row['Listing Key'];
                        const pend = pendingEdits[lk] && pendingEdits[lk][meta.field];
                        const current = Array.isArray(pend)
                          ? pend
                          : (v ? String(v).split(', ').filter(Boolean) : []);
                        const openId = row._id + ':' + col;
                        const isOpen = multiEditor === openId;
                        const options = meta.vocab ? ((attrOptions && attrOptions[meta.vocab]) || []) : null;
                        const setSel = (list) => applyMultiEdit(lk, col, meta.field, list);
                        return (
                          <td key={col} className="editable-cell" style={{ ...styles.dataCell, overflow: 'visible', whiteSpace: 'normal' }}>
                            <div
                              onClick={(e) => { setMenuRect(e.currentTarget.getBoundingClientRect()); setMultiEditor(isOpen ? null : openId); setTagInput(''); }}
                              style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center', cursor: 'pointer', minHeight: 22 }}
                            >
                              {current.length === 0 && <span style={{ color: t.text3, fontSize: 11 }}>Click to edit</span>}
                              {current.map((x) => (
                                <span key={x} style={{ background: ACCENT_LIGHT, color: ACCENT_DARK, borderRadius: 10, padding: '1px 8px', fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                  {x}
                                  <span onClick={(e) => { e.stopPropagation(); setSel(current.filter((c) => c !== x)); }} style={{ cursor: 'pointer', fontWeight: 700 }}>×</span>
                                </span>
                              ))}
                              <span style={{ color: t.text3, fontSize: 10 }}>▾</span>
                            </div>
                            {isOpen && (
                              <>
                                <div style={styles.overlay} onClick={() => { setMultiEditor(null); setTagInput(''); }} />
                                <div style={{ ...styles.dropdown, ...menuStyle(menuRect, 220) }} onClick={(e) => e.stopPropagation()}>
                                  {options ? (
                                    options.map((o) => {
                                      const checked = current.includes(o);
                                      return (
                                        <label key={o} style={styles.checkRow}>
                                          <input
                                            type="checkbox"
                                            style={styles.checkbox}
                                            checked={checked}
                                            onChange={() => setSel(checked ? current.filter((c) => c !== o) : [...current, o])}
                                          />
                                          {o}
                                        </label>
                                      );
                                    })
                                  ) : (
                                    <div style={{ padding: 8 }}>
                                      <input
                                        value={tagInput}
                                        onChange={(e) => setTagInput(e.target.value)}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') {
                                            const tnew = tagInput.trim();
                                            if (tnew && !current.includes(tnew)) setSel([...current, tnew]);
                                            setTagInput('');
                                          }
                                        }}
                                        placeholder="Add tag + Enter"
                                        maxLength={20}
                                        style={{ width: '100%', fontSize: 12, padding: '4px 6px', border: `1px solid ${t.border}`, borderRadius: 3, background: t.card, color: t.text }}
                                      />
                                    </div>
                                  )}
                                </div>
                              </>
                            )}
                          </td>
                        );
                      }

                      // Owner: multi-select cell with chips + dropdown
                      if (col === 'Owner') {
                        const owners = ownersOf(v);
                        const isOpen = ownerEditor === row._id;
                        const isSelected = selectedCell?.rowId === row._id && selectedCell?.col === col;
                        const inFillRange = (() => {
                          if (!fillDrag || fillDrag.col !== col) return false;
                          const sIdx = visible.findIndex((r) => r._id === fillDrag.sourceRowId);
                          const tIdx = visible.findIndex((r) => r._id === fillDrag.targetRowId);
                          if (sIdx === -1 || tIdx === -1) return false;
                          const lo = Math.min(sIdx, tIdx), hi = Math.max(sIdx, tIdx);
                          return visIdx >= lo && visIdx <= hi;
                        })();
                        const justFilled = justFilledIds.includes(row._id) && fillDrag === null;
                        return (
                          <td
                            key={col}
                            className="data-cell"
                            style={{
                              ...styles.dataCell,
                              padding: '6px 10px',
                              cursor: 'pointer',
                              overflow: 'visible',
                              whiteSpace: 'normal',
                              position: 'relative',
                              background: isOpen
                                ? '#f8fbf9'
                                : inFillRange
                                ? 'rgba(16,124,65,0.08)'
                                : justFilled
                                ? 'rgba(16,124,65,0.15)'
                                : undefined,
                              outline: isSelected
                                ? `2px solid ${ACCENT}`
                                : inFillRange
                                ? `1px dashed ${ACCENT}`
                                : 'none',
                              outlineOffset: isSelected ? '-2px' : '-1px',
                              transition: justFilled ? 'background 0.5s ease' : undefined,
                              zIndex: isSelected || isOpen ? 2 : inFillRange ? 1 : 'auto',
                            }}
                            onClick={() => {
                              setSelectedCell({ rowId: row._id, col });
                              setOwnerEditor(isOpen ? null : row._id);
                            }}
                            onMouseEnter={() => {
                              if (fillDrag && fillDrag.col === col) {
                                setFillDrag({ ...fillDrag, targetRowId: row._id });
                              }
                            }}
                          >
                            <div style={styles.ownerCellInner}>
                              {owners.length === 0 && (
                                <span style={styles.ownerPlaceholder}>Click to assign</span>
                              )}
                              {owners.map((o) => (
                                <span
                                  key={o}
                                  style={{
                                    ...styles.ownerChip,
                                    background: ownerColor(o),
                                    marginLeft: owners.indexOf(o) > 0 ? -8 : 0,
                                  }}
                                  title={o}
                                >
                                  {ownerInitials(o)}
                                </span>
                              ))}
                              <span style={styles.ownerCellCaret}>▾</span>
                            </div>
                            {isSelected && !isOpen && (
                              <div
                                style={styles.fillHandle}
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setFillDrag({ col, sourceRowId: row._id, targetRowId: row._id });
                                }}
                                onClick={(e) => e.stopPropagation()}
                                title="Drag to fill"
                              />
                            )}
                            {isOpen && (
                              <>
                                <div
                                  style={styles.overlay}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOwnerEditor(null);
                                    setNewOwnerInput('');
                                  }}
                                />
                                <div
                                  style={{ ...styles.dropdown, left: 0, right: 'auto', minWidth: 200 }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {allOwners().length === 0 && (
                                    <div style={{ padding: '12px', color: '#888', fontSize: 12, fontStyle: 'italic' }}>
                                      No owners yet — add one below
                                    </div>
                                  )}
                                  {allOwners().map((o) => (
                                    <label key={o} style={styles.checkRow}>
                                      <input
                                        type="checkbox"
                                        style={styles.checkbox}
                                        checked={owners.includes(o)}
                                        onChange={() => toggleOwnerForRow(row._id, o)}
                                      />
                                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: "'Manrope', sans-serif" }}>
                                        <span
                                          style={{
                                            ...styles.ownerChip,
                                            background: ownerColor(o),
                                            width: 20,
                                            height: 20,
                                            fontSize: 9,
                                            border: 'none',
                                            boxShadow: 'none',
                                          }}
                                        >
                                          {ownerInitials(o)}
                                        </span>
                                        {o}
                                      </span>
                                    </label>
                                  ))}
                                  <div style={styles.addRow}>
                                    <input
                                      style={styles.addInput}
                                      placeholder="Add new owner…"
                                      value={newOwnerInput}
                                      onChange={(e) => setNewOwnerInput(e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') addOwnerToRow(row._id);
                                      }}
                                    />
                                    <button style={styles.addBtn} onClick={() => addOwnerToRow(row._id)}>
                                      +
                                    </button>
                                  </div>
                                </div>
                              </>
                            )}
                          </td>
                        );
                      }

                      const isEditing = editing?.rowId === row._id && editing?.col === col;
                      const isSelected = selectedCell?.rowId === row._id && selectedCell?.col === col;
                      const inFillRange = (() => {
                        if (!fillDrag || fillDrag.col !== col) return false;
                        const sIdx = visible.findIndex((r) => r._id === fillDrag.sourceRowId);
                        const tIdx = visible.findIndex((r) => r._id === fillDrag.targetRowId);
                        if (sIdx === -1 || tIdx === -1) return false;
                        const lo = Math.min(sIdx, tIdx), hi = Math.max(sIdx, tIdx);
                        return visIdx >= lo && visIdx <= hi;
                      })();
                      const justFilled = justFilledIds.includes(row._id) && fillDrag === null;
                      return (
                        <td
                          key={col}
                          className="data-cell"
                          style={{
                            ...styles.dataCell,
                            textAlign: isNumericCol(col) ? 'right' : 'left',
                            padding: isEditing ? 0 : styles.dataCell.padding,
                            position: col === 'Task' ? 'sticky' : 'relative',
                            left: col === 'Task' ? 40 : undefined,
                            zIndex: isSelected ? 2 : inFillRange ? 1 : (col === 'Task' ? 1 : 'auto'),
                            outline: isSelected
                              ? `2px solid ${ACCENT}`
                              : inFillRange
                              ? `1px dashed ${ACCENT}`
                              : 'none',
                            outlineOffset: isSelected ? '-2px' : '-1px',
                            background: inFillRange
                              ? 'rgba(16,124,65,0.08)'
                              : justFilled
                              ? 'rgba(16,124,65,0.15)'
                              : (col === 'Task' ? t.card : (heatColor(v, col) || undefined)),
                            transition: justFilled ? 'background 0.5s ease' : undefined,
                          }}
                          onClick={() => setSelectedCell({ rowId: row._id, col })}
                          onDoubleClick={() => startEdit(row._id, col, v)}
                          onMouseEnter={() => {
                            if (fillDrag && fillDrag.col === col) {
                              setFillDrag({ ...fillDrag, targetRowId: row._id });
                            }
                          }}
                        >
                          {isEditing ? (
                            <input
                              ref={inputRef}
                              style={styles.editInput}
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              onBlur={commitEdit}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') commitEdit();
                                if (e.key === 'Escape') { setEditing(null); setEditValue(''); }
                              }}
                            />
                          ) : col === 'Status' ? (
                            <span style={styles.statusPill(v)}>{v}</span>
                          ) : col === 'Priority' ? (
                            <><span style={styles.priorityDot(v)}></span>{v}</>
                          ) : (
                            fmt(v, col)
                          )}
                          {isSelected && !isEditing && (
                            <div
                              style={styles.fillHandle}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setFillDrag({ col, sourceRowId: row._id, targetRowId: row._id });
                              }}
                              title="Drag to fill"
                            />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
                };
                if (groupedRows) {
                  return groupedRows.slice(0, renderLimit).map((item) => {
                    if (item.type === 'group') return renderGroupHeader(item);
                    const visIdx = idToVisIdx.get(item.row._id) ?? -1;
                    return renderDataRow(item.row, visIdx);
                  });
                }
                return visible.slice(0, renderLimit).map((row, visIdx) => renderDataRow(row, visIdx));
              })()}
              {totalRenderRows > renderLimit && (
                <tr key="__load_more">
                  <td
                    colSpan={displayHeaders.length + 1}
                    onClick={() => setRenderLimit((l) => Math.min(l + ROW_RENDER_CHUNK, totalRenderRows))}
                    style={{ ...styles.dataCell, textAlign: 'center', padding: '12px', cursor: 'pointer', color: t.text2, fontStyle: 'italic' }}
                  >
                    Showing {renderLimit.toLocaleString()} of {totalRenderRows.toLocaleString()} rows — scroll or click to load more (charts use all rows)
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div style={styles.statusBar}>
          <div>
            {visible.length} of {data.length} rows
            {visible.length > renderLimit && ` · rendering first ${renderLimit.toLocaleString()}`}
            {filterIds !== null && ' · ai filtered'}
            {Object.keys(colFilters).length > 0 && ` · ${Object.keys(colFilters).join(', ')} filtered`}
            {Object.keys(numFilters).filter(isNumFiltered).map((c) => ` · ${c} ${numFilters[c].op} ${numFilters[c].value}`).join('')}
            {sortCol && ` · sorted by ${sortCol} ${sortDir === 'asc' ? '↑' : '↓'}`}
            {groupBy.length > 0 && ` · grouped by ${groupBy.join(' → ')}`}
          </div>
          <div>Sheet1</div>
        </div>

      </div>
    </div>
  );
}
